import { useState, useEffect, useRef } from 'react';
import { Usd, PriceCaption, useCurrency } from '@/utils/CurrencyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabWithTooltip } from '@/components/ui/tab-with-tooltip';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import api from '@/utils/api';
import { uploadImageToS3, checkS3Status } from '@/utils/s3Upload';
import { toast } from 'sonner';
import { Copy, ArrowLeft, DollarSign, Users, TrendingUp, Settings, Percent, RefreshCw, Building2, CheckCircle, AlertCircle, Eye, CreditCard, Globe, Edit2, RotateCcw, UserX, UserCheck, LogIn, MessageCircle, Send, GraduationCap, Clock, CalendarPlus, Package, ShoppingBag, XCircle, Shield, Trash2, UserPlus, Plus, Upload, Cloud, User, Mail, BookOpen, ChevronRight, Bell, Store, Megaphone, FileText, Check, X, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PlatformSignatureCard } from '@/components/admin/PlatformSignatureCard';
import { PendingModelLettersCard } from '@/components/admin/PendingModelLettersCard';
import { CourseOrderCard } from '@/components/admin/CourseOrderCard';
import { DuplicateCourseDialog } from '@/components/admin/DuplicateCourseDialog';

const AdminDashboard = () => {
  const { formatUsdZar } = useCurrency();
  // URL-based tab routing
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['payouts', 'settings', 'currencies', 'teachers', 'students', 'sellers', 'courses', 'enrollments', 'kits', 'products', 'declarations', 'messages', 'admins'];
  const tabFromUrl = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'payouts';
  
  const setActiveTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const [analytics, setAnalytics] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [sellerPayouts, setSellerPayouts] = useState([]);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [adminBankDetails, setAdminBankDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGlobalCommission, setShowGlobalCommission] = useState(false);
  const [showAdminBankForm, setShowAdminBankForm] = useState(false);
  const [showTeacherCommission, setShowTeacherCommission] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState(null);
  const [showPayoutDialog, setShowPayoutDialog] = useState(null);
  const [showSellerPayoutDialog, setShowSellerPayoutDialog] = useState(null);
  const [showSellerBankDetails, setShowSellerBankDetails] = useState(null);
  const [sellerBankDetailsData, setSellerBankDetailsData] = useState(null);
  const [bankDetailsData, setBankDetailsData] = useState(null);
  const [newCommission, setNewCommission] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);  // Teacher detail panel
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [editCurrencyRate, setEditCurrencyRate] = useState('');
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [chatUsers, setChatUsers] = useState([]);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [enrollments, setEnrollments] = useState([]);
  const [showExtendDialog, setShowExtendDialog] = useState(null);
  const [extensionDays, setExtensionDays] = useState('30');
  const [courseKits, setCourseKits] = useState([]);
  const [selectedKit, setSelectedKit] = useState(null);
  const [kitStatus, setKitStatus] = useState('');
  const [kitNotes, setKitNotes] = useState('');
  const [pendingProducts, setPendingProducts] = useState([]);
  const [marketplaceOrders, setMarketplaceOrders] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [duplicatingCourse, setDuplicatingCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseEditPrice, setCourseEditPrice] = useState('');
  const [courseEditNotes, setCourseEditNotes] = useState('');
  const [landingStatsEdit, setLandingStatsEdit] = useState({ views: '300K+', enrollments: '500+' });
  const [savingStats, setSavingStats] = useState(false);
  const [adminBankForm, setAdminBankForm] = useState({
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    routing_number: '',
    swift_code: ''
  });
  
  // Admin management state
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [promotableUsers, setPromotableUsers] = useState([]);
  const [selectedUserToPromote, setSelectedUserToPromote] = useState('');
  const [promotableSearchQuery, setPromotableSearchQuery] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  
  // Admin Product Creation state
  const [showCreateProductDialog, setShowCreateProductDialog] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [selectedProductImage, setSelectedProductImage] = useState(null);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [storageConfigured, setStorageConfigured] = useState(false);
  const [productFormData, setProductFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: 'books',
    stock: '',
    is_digital: false,
    download_url: ''
  });
  const productImageInputRef = useRef(null);
  const notificationRef = useRef(null);
  
  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Slot Management state
  const [showSlotManagement, setShowSlotManagement] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [slotConfig, setSlotConfig] = useState({ featured_total_slots: 6, essential_total_slots: 6 });
  const [featuredMode, setFeaturedMode] = useState('automatic');
  const [essentialMode, setEssentialMode] = useState('automatic');
  const [selectedProductForSlot, setSelectedProductForSlot] = useState(null);
  const [selectedSection, setSelectedSection] = useState('featured');
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(1);
  const [isAssigningSlot, setIsAssigningSlot] = useState(false);
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  
  // Teacher Announcements state
  const [pendingAnnouncements, setPendingAnnouncements] = useState([]);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  
  // Pending Declarations state
  const [pendingDeclarations, setPendingDeclarations] = useState([]);
  const [isApprovingDeclaration, setIsApprovingDeclaration] = useState(null);
  const [isRejectingDeclaration, setIsRejectingDeclaration] = useState(null);
  
  // All Products Management state
  const [allProducts, setAllProducts] = useState([]);
  const [showEditProductDialog, setShowEditProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductFormData, setEditProductFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: 'books',
    stock: '',
    is_digital: false,
    download_url: ''
  });
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    fetchLandingStats();
    fetchAdminUsers();
    fetchNotifications();
    // Check R2/S3 storage status
    checkS3Status().then(status => {
      setStorageConfigured(status.configured);
    });
    
    // Poll for new notifications every 30 seconds
    const notificationInterval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(notificationInterval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/admin/notifications?unread_only=false&limit=20');
      setNotifications(res.data.notifications || []);
      setUnreadNotificationCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const res = await api.get('/admin/notifications/count');
      setUnreadNotificationCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await api.patch(`/admin/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await api.patch('/admin/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadNotificationCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await api.delete(`/admin/notifications/${notificationId}`);
      const removed = notifications.find(n => n.id === notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      if (removed && !removed.read) {
        setUnreadNotificationCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      toast.error('Failed to remove notification');
    }
  };

  const fetchLandingStats = async () => {
    try {
      const res = await api.get('/landing-stats');
      setLandingStatsEdit(res.data);
    } catch (error) {
      console.error('Failed to fetch landing stats:', error);
    }
  };

  const handleUpdateLandingStats = async () => {
    setSavingStats(true);
    try {
      await api.put('/landing-stats', landingStatsEdit);
      toast.success('Stats updated successfully');
    } catch (error) {
      toast.error('Failed to update stats');
    } finally {
      setSavingStats(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdminUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    }
  };

  const fetchPromotableUsers = async () => {
    try {
      const res = await api.get('/admin/promotable-users');
      setPromotableUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch promotable users:', error);
    }
  };

  const handleOpenPromoteDialog = () => {
    fetchPromotableUsers();
    setShowPromoteDialog(true);
  };

  const handlePromoteToAdmin = async () => {
    if (!selectedUserToPromote) {
      toast.error('Please select a user to promote');
      return;
    }
    
    setIsPromoting(true);
    try {
      await api.post('/admin/promote-to-admin', {
        user_id: selectedUserToPromote
      });
      toast.success('User promoted to admin successfully');
      setShowPromoteDialog(false);
      setSelectedUserToPromote('');
      setPromotableSearchQuery('');
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    } finally {
      setIsPromoting(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdminEmail || !newAdminName || !newAdminPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newAdminPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setIsCreatingAdmin(true);
    try {
      await api.post('/admin/admins', {
        email: newAdminEmail,
        name: newAdminName,
        password: newAdminPassword
      });
      toast.success('Admin account created successfully');
      setShowAddAdminDialog(false);
      setNewAdminEmail('');
      setNewAdminName('');
      setNewAdminPassword('');
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!confirm(`Are you sure you want to remove admin access for ${adminEmail}?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/admins/${adminId}`);
      toast.success('Admin removed successfully');
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const fetchData = async () => {
    try {
      const [analyticsRes, teachersRes, studentsRes, sellersRes, settingsRes, payoutsRes, sellerPayoutsRes, adminBankRes, currenciesRes] = await Promise.all([
        api.get('/admin/commission-analytics'),
        api.get('/admin/teachers'),
        api.get('/admin/students'),
        api.get('/admin/sellers'),
        api.get('/admin/platform-settings'),
        api.get('/admin/payouts'),
        api.get('/admin/seller-payouts'),
        api.get('/admin/bank-details'),
        api.get('/admin/currencies')
      ]);
      
      setAnalytics(analyticsRes.data);
      setTeachers(teachersRes.data);
      setStudents(studentsRes.data);
      setSellers(sellersRes.data);
      setPlatformSettings(settingsRes.data);
      setPayouts(payoutsRes.data);
      setSellerPayouts(sellerPayoutsRes.data);
      setAdminBankDetails(adminBankRes.data);
      setCurrencies(currenciesRes.data.currencies || []);
      
      if (adminBankRes.data.has_bank_details) {
        setAdminBankForm({
          bank_name: adminBankRes.data.bank_name || '',
          account_holder_name: adminBankRes.data.account_holder_name || '',
          account_number: '',
          routing_number: adminBankRes.data.routing_number || '',
          swift_code: adminBankRes.data.swift_code || ''
        });
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateGlobalCommission = async () => {
    const rate = parseFloat(newCommission) / 100;
    if (isNaN(rate) || rate < 0 || rate > 1) {
      toast.error('Please enter a valid percentage (0-100)');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.put('/admin/platform-settings/commission', { commission_rate: rate });
      toast.success('Global commission rate updated');
      setShowGlobalCommission(false);
      setNewCommission('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update commission rate');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveAdminBankDetails = async () => {
    if (!adminBankForm.bank_name || !adminBankForm.account_holder_name || !adminBankForm.account_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.post('/admin/bank-details', adminBankForm);
      toast.success('Platform bank details saved successfully!');
      setShowAdminBankForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save bank details');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateTeacherCommission = async (teacherId) => {
    const rate = parseFloat(newCommission) / 100;
    if (isNaN(rate) || rate < 0 || rate > 1) {
      toast.error('Please enter a valid percentage (0-100)');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.put(`/admin/teachers/${teacherId}/commission`, { commission_rate: rate });
      toast.success('Teacher commission rate updated');
      setShowTeacherCommission(null);
      setNewCommission('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update commission rate');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleViewBankDetails = async (teacherId) => {
    try {
      const response = await api.get(`/admin/teachers/${teacherId}/bank-details`);
      setBankDetailsData(response.data);
      setShowBankDetails(teacherId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load bank details');
    }
  };

  const handleVerifyBank = async (teacherId) => {
    try {
      await api.put(`/admin/teachers/${teacherId}/verify-bank`);
      toast.success('Bank details verified');
      fetchData();
      setShowBankDetails(null);
    } catch (error) {
      toast.error('Failed to verify bank details');
    }
  };

  const handleCreatePayout = async (teacherId) => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.post('/admin/payouts', {
        teacher_id: teacherId,
        amount: amount,
        notes: payoutNotes
      });
      toast.success('Payout recorded successfully');
      setShowPayoutDialog(null);
      setPayoutAmount('');
      setPayoutNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payout');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateSellerPayout = async (sellerId) => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.post('/admin/seller-payouts', {
        seller_id: sellerId,
        amount: amount,
        notes: payoutNotes
      });
      toast.success('Seller payout recorded successfully');
      setShowSellerPayoutDialog(null);
      setPayoutAmount('');
      setPayoutNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record seller payout');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleViewSellerBankDetails = async (sellerId) => {
    try {
      const response = await api.get(`/admin/seller-payouts/${sellerId}/bank-details`);
      setSellerBankDetailsData(response.data);
      setShowSellerBankDetails(sellerId);
    } catch (error) {
      toast.error('Failed to load seller bank details');
    }
  };

  const handleUpdateCurrencyRate = async (currencyCode) => {
    const rate = parseFloat(editCurrencyRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Please enter a valid rate');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.put(`/admin/currencies/${currencyCode}`, {
        currency_code: currencyCode,
        rate: rate,
        is_active: true
      });
      toast.success(`Rate for ${currencyCode} updated successfully`);
      setEditingCurrency(null);
      setEditCurrencyRate('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update currency rate');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleCurrency = async (currency) => {
    setIsUpdating(true);
    try {
      await api.put(`/admin/currencies/${currency.code}`, {
        currency_code: currency.code,
        rate: currency.effective_rate,
        is_active: !currency.is_active
      });
      toast.success(`${currency.code} ${!currency.is_active ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update currency');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResetToLiveRate = async (currencyCode) => {
    setIsUpdating(true);
    try {
      await api.delete(`/admin/currencies/${currencyCode}/manual`);
      toast.success(`${currencyCode} reset to live exchange rate`);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset currency rate');
    } finally {
      setIsUpdating(false);
    }
  };

  // User account management
  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!currentStatus) {
      // Activating - no reason needed
      setIsUpdating(true);
      try {
        await api.put(`/admin/users/${userId}/status`, { is_active: true });
        toast.success('Account activated successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to update account status');
      } finally {
        setIsUpdating(false);
      }
    } else {
      // Deactivating - show dialog to get reason
      setShowDeactivateDialog(userId);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!showDeactivateDialog) return;
    
    setIsUpdating(true);
    try {
      await api.put(`/admin/users/${showDeactivateDialog}/status`, {
        is_active: false,
        reason: deactivationReason
      });
      toast.success('Account deactivated successfully');
      setShowDeactivateDialog(null);
      setDeactivationReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate account');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async (userId, userName, userRole) => {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE the ${userRole} account "${userName}"?\n\nThis will delete:\n• All their courses and lessons (for teachers)\n• All enrollments and progress (for students)\n• All products (for sellers)\n• All payment records\n• All notifications and messages\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    // Double confirm for safety
    if (!confirm(`FINAL CONFIRMATION: Delete "${userName}" permanently?`)) {
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`Account "${userName}" has been permanently deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      // Store admin token for returning later
      localStorage.setItem('admin_token', localStorage.getItem('token'));
      // Set new token
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('is_impersonation', 'true');
      
      toast.success(`Now viewing as ${response.data.user.name}`);
      
      // Navigate to appropriate dashboard
      if (response.data.user.role === 'teacher') {
        navigate('/teacher');
      } else if (response.data.user.role === 'student') {
        navigate('/student');
      }
      window.location.reload();
    } catch (error) {
      toast.error('Failed to impersonate user');
    }
  };

  // Admin chat functions
  const fetchChatUsers = async () => {
    try {
      const response = await api.get('/admin/chat/users');
      setChatUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch chat users:', error);
    }
  };

  const handleSelectChatUser = async (user) => {
    setSelectedChatUser(user);
    try {
      const response = await api.get(`/admin/chat/${user.id}/messages`);
      setChatMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatUser) return;
    
    try {
      await api.post('/admin/chat/send', {
        user_id: selectedChatUser.id,
        message: newMessage
      });
      setNewMessage('');
      // Refresh messages
      const response = await api.get(`/admin/chat/${selectedChatUser.id}/messages`);
      setChatMessages(response.data.messages || []);
      fetchChatUsers(); // Update unread counts
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Enrollment management functions
  const fetchEnrollments = async () => {
    try {
      const response = await api.get('/admin/enrollments');
      setEnrollments(response.data);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    }
  };

  const handleExtendEnrollment = async (enrollmentId) => {
    const days = parseInt(extensionDays);
    if (isNaN(days) || days < 1 || days > 365) {
      toast.error('Please enter a valid number of days (1-365)');
      return;
    }
    
    setIsUpdating(true);
    try {
      const response = await api.put(`/admin/enrollments/${enrollmentId}/extend`, { days });
      toast.success(response.data.message);
      setShowExtendDialog(null);
      setExtensionDays('30');
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend enrollment');
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchChatUsers();
    }
    if (activeTab === 'enrollments') {
      fetchEnrollments();
    }
    if (activeTab === 'kits') {
      fetchCourseKits();
    }
    if (activeTab === 'courses') {
      fetchAllCourses();
    }
    if (activeTab === 'products') {
      fetchPendingProducts();
      fetchMarketplaceOrders();
      fetchAllProducts();
      fetchPendingAnnouncements();
      fetchAllAnnouncements();
    }
    if (activeTab === 'declarations') {
      fetchPendingDeclarations();
    }
  }, [activeTab]);

  // Marketplace Product Management
  const fetchPendingProducts = async () => {
    try {
      const response = await api.get('/marketplace/admin/pending');
      setPendingProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch pending products:', error);
    }
  };

  const fetchMarketplaceOrders = async () => {
    try {
      const response = await api.get('/marketplace/admin/orders?limit=20');
      setMarketplaceOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch marketplace orders:', error);
    }
  };

  // Fetch all approved products for admin management
  const fetchAllProducts = async () => {
    try {
      const response = await api.get('/marketplace/products');
      setAllProducts(response.data?.products || []);
    } catch (error) {
      console.error('Failed to fetch all products:', error);
    }
  };

  // Teacher Announcements Management
  const fetchPendingAnnouncements = async () => {
    try {
      const response = await api.get('/announcements/admin/pending');
      setPendingAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch pending announcements:', error);
    }
  };

  const fetchAllAnnouncements = async () => {
    try {
      const response = await api.get('/announcements/admin/all');
      setAllAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch all announcements:', error);
    }
  };

  const handleApproveAnnouncement = async (id) => {
    try {
      await api.post(`/announcements/admin/${id}/approve`);
      toast.success('Announcement approved');
      fetchPendingAnnouncements();
      fetchAllAnnouncements();
    } catch (error) {
      console.error('Failed to approve announcement:', error);
      toast.error('Failed to approve announcement');
    }
  };

  const handleRejectAnnouncement = async (id) => {
    try {
      await api.post(`/announcements/admin/${id}/reject`);
      toast.success('Announcement rejected');
      fetchPendingAnnouncements();
      fetchAllAnnouncements();
    } catch (error) {
      console.error('Failed to reject announcement:', error);
      toast.error('Failed to reject announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await api.delete(`/announcements/admin/${id}`);
      toast.success('Announcement deleted');
      fetchPendingAnnouncements();
      fetchAllAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  // Declarations Management
  const fetchPendingDeclarations = async () => {
    try {
      const response = await api.get('/declarations/admin/pending');
      setPendingDeclarations(response.data.declarations || []);
    } catch (error) {
      console.error('Failed to fetch pending declarations:', error);
    }
  };

  const handleApproveDeclaration = async (id) => {
    setIsApprovingDeclaration(id);
    try {
      await api.post(`/declarations/admin/${id}/approve`);
      toast.success('Declaration approved');
      fetchPendingDeclarations();
    } catch (error) {
      console.error('Failed to approve declaration:', error);
      toast.error('Failed to approve declaration');
    } finally {
      setIsApprovingDeclaration(null);
    }
  };

  const handleRejectDeclaration = async (id) => {
    setIsRejectingDeclaration(id);
    try {
      await api.post(`/declarations/admin/${id}/reject`);
      toast.success('Declaration rejected');
      fetchPendingDeclarations();
    } catch (error) {
      console.error('Failed to reject declaration:', error);
      toast.error('Failed to reject declaration');
    } finally {
      setIsRejectingDeclaration(null);
    }
  };

  // Edit Product functions
  const openEditProductDialog = (product) => {
    setEditingProduct(product);
    setEditProductFormData({
      title: product.title || product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      category: product.category || 'books',
      stock: product.stock?.toString() || '',
      is_digital: product.is_digital || false,
      download_url: product.download_url || ''
    });
    setShowEditProductDialog(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    
    setIsUpdatingProduct(true);
    try {
      await api.put(`/marketplace/products/${editingProduct.id}`, {
        title: editProductFormData.title,
        description: editProductFormData.description,
        price: parseFloat(editProductFormData.price),
        category: editProductFormData.category,
        stock: parseInt(editProductFormData.stock) || 0,
        is_digital: editProductFormData.is_digital,
        download_url: editProductFormData.is_digital ? editProductFormData.download_url : null
      });
      toast.success('Product updated!');
      setShowEditProductDialog(false);
      setEditingProduct(null);
      fetchAllProducts();
      fetchPendingProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId, productTitle) => {
    if (!confirm(`Are you sure you want to delete "${productTitle}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/marketplace/products/${productId}`);
      toast.success('Product deleted');
      fetchAllProducts();
      fetchPendingProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleApproveProduct = async (productId) => {
    try {
      await api.post(`/marketplace/admin/approve/${productId}`);
      toast.success('Product approved!');
      fetchPendingProducts();
    } catch (error) {
      toast.error('Failed to approve product');
    }
  };

  const handleRejectProduct = async (productId) => {
    const reason = prompt('Reason for rejection (optional):');
    try {
      await api.post(`/marketplace/admin/reject/${productId}`, null, {
        params: { reason }
      });
      toast.success('Product rejected');
      fetchPendingProducts();
    } catch (error) {
      toast.error('Failed to reject product');
    }
  };

  // Admin Product Creation functions
  const handleProductImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      setSelectedProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetProductForm = () => {
    setProductFormData({
      title: '',
      description: '',
      price: '',
      category: 'books',
      stock: '',
      is_digital: false,
      download_url: ''
    });
    setSelectedProductImage(null);
    setProductImagePreview(null);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (!productFormData.title || !productFormData.description || !productFormData.price || !productFormData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreatingProduct(true);
    setImageUploadProgress(0);
    
    try {
      let imageUrl = null;
      
      // Upload image to R2 first if selected
      if (selectedProductImage) {
        if (storageConfigured) {
          try {
            toast.info('Uploading image to cloud storage...');
            const uploadResult = await uploadImageToS3(
              selectedProductImage, 
              'products/',
              (progress) => setImageUploadProgress(progress)
            );
            imageUrl = uploadResult.url;
          } catch (imgError) {
            console.error('R2 upload failed:', imgError);
            toast.error('Image upload failed. Creating product without image.');
          }
        } else {
          // Fallback to local upload if R2 not configured
          toast.warning('Cloud storage not configured. Using local storage.');
        }
      }
      
      const payload = {
        title: productFormData.title,
        description: productFormData.description,
        price: parseFloat(productFormData.price),
        category: productFormData.category,
        stock: parseInt(productFormData.stock) || 0,
        is_digital: productFormData.is_digital,
        download_url: productFormData.is_digital ? productFormData.download_url : null,
        images: imageUrl ? [imageUrl] : []
      };

      // Admin creates product - it's auto-approved
      const response = await api.post('/marketplace/admin/products', payload);
      const productId = response.data.id;
      
      // If R2 wasn't configured and we have an image, try local upload
      if (selectedProductImage && !imageUrl && productId) {
        const imageFormData = new FormData();
        imageFormData.append('file', selectedProductImage);
        try {
          await api.post(`/marketplace/products/${productId}/images`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (imgError) {
          console.error('Local image upload failed:', imgError);
          toast.error('Product created but image upload failed');
        }
      }

      toast.success('Product created and approved!');
      setShowCreateProductDialog(false);
      resetProductForm();
      fetchPendingProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setIsCreatingProduct(false);
      setImageUploadProgress(0);
    }
  };

  // Slot Management functions
  const fetchProductsForSlots = async () => {
    try {
      const res = await api.get('/landing-slots/products-for-assignment');
      setAvailableProducts(res.data.products || []);
      setSlotConfig({
        featured_total_slots: res.data.featured_total_slots || 6,
        essential_total_slots: res.data.essential_total_slots || 6
      });
    } catch (error) {
      console.error('Failed to fetch products for slots:', error);
    }
  };

  const fetchSlotModes = async () => {
    try {
      const [featuredRes, essentialRes] = await Promise.all([
        api.get('/landing-slots/featured'),
        api.get('/landing-slots/new_arrivals')
      ]);
      setFeaturedMode(featuredRes.data.mode || 'automatic');
      setEssentialMode(essentialRes.data.mode || 'automatic');
    } catch (error) {
      console.error('Failed to fetch slot modes:', error);
    }
  };

  const handleOpenSlotManagement = () => {
    fetchProductsForSlots();
    fetchSlotModes();
    setShowSlotManagement(true);
  };

  const handleUpdateSlotMode = async (section, newMode) => {
    setIsUpdatingMode(true);
    try {
      await api.put(`/landing-slots/${section}/config`, { mode: newMode });
      if (section === 'featured') {
        setFeaturedMode(newMode);
      } else {
        setEssentialMode(newMode);
      }
      toast.success(`${section === 'featured' ? 'Featured Products' : 'Essential Class'} mode changed to ${newMode}`);
      fetchProductsForSlots();
    } catch (error) {
      toast.error('Failed to update mode');
    } finally {
      setIsUpdatingMode(false);
    }
  };

  const handleAssignProductToSlot = async () => {
    if (!selectedProductForSlot) {
      toast.error('Please select a product');
      return;
    }
    
    setIsAssigningSlot(true);
    try {
      await api.post(`/landing-slots/${selectedSection}/assign-product`, {
        product_id: selectedProductForSlot,
        slot_number: selectedSlotNumber
      });
      toast.success(`Product assigned to ${selectedSection} slot ${selectedSlotNumber}`);
      fetchProductsForSlots();
      setSelectedProductForSlot(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign product');
    } finally {
      setIsAssigningSlot(false);
    }
  };

  const handleRemoveOverride = async (section, slotNumber) => {
    try {
      await api.delete(`/landing-slots/${section}/slot/${slotNumber}/override`);
      toast.success('Manual override removed - slot will auto-fill');
      fetchProductsForSlots();
    } catch (error) {
      toast.error('Failed to remove override');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/marketplace/admin/orders/${orderId}/status`, null, {
        params: { status }
      });
      toast.success(`Order status updated to ${status}`);
      fetchMarketplaceOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // Course Kits management functions
  const fetchCourseKits = async () => {
    try {
      const response = await api.get('/admin/kits');
      setCourseKits(response.data);
    } catch (error) {
      console.error('Failed to fetch course kits:', error);
    }
  };

  // All Courses management functions
  const fetchAllCourses = async () => {
    try {
      const response = await api.get('/admin/courses');
      setAllCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleUpdateCoursePrice = async (courseId) => {
    const price = parseFloat(courseEditPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.put(`/admin/courses/${courseId}`, {
        price: price,
        admin_notes: courseEditNotes
      });
      toast.success('Course price updated! Teacher has been notified.');
      setSelectedCourse(null);
      setCourseEditPrice('');
      setCourseEditNotes('');
      fetchAllCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course price');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateKitStatus = async (kitId) => {
    if (!kitStatus) {
      toast.error('Please select a status');
      return;
    }
    
    setIsUpdating(true);
    try {
      await api.put(`/admin/kits/${kitId}/status?status=${kitStatus}&notes=${encodeURIComponent(kitNotes)}`);
      toast.success('Kit status updated');
      setSelectedKit(null);
      setKitStatus('');
      setKitNotes('');
      fetchCourseKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update kit status');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent mx-auto mb-4" style={{ borderColor: '#163329', borderTopColor: 'transparent' }}></div>
            <Shield className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6" style={{ color: '#163329' }} />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Admin Dashboard</h2>
          <p style={{ color: '#5C635A' }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="mb-8 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1
              className="text-4xl font-bold tracking-tight mb-2"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              Admin Dashboard
            </h1>
            <p style={{ color: '#5C635A' }}>Manage platform commissions, teachers, and payouts</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Notifications Bell */}
            <div className="relative" ref={notificationRef}>
              <Button
                variant="outline"
                className="rounded-full relative text-xs sm:text-sm"
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-bell"
              >
                <Bell className="h-4 w-4 mr-1.5" />
                <span>Alerts</span>
                {unreadNotificationCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 h-5 w-5 min-w-[20px] rounded-full flex items-center justify-center text-xs font-bold text-white ring-2 ring-white animate-pulse"
                    style={{ backgroundColor: '#D86B47' }}
                  >
                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                  </span>
                )}
              </Button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <div 
                  className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-80 max-h-96 overflow-y-auto rounded-lg shadow-lg border z-50"
                  style={{ backgroundColor: '#FFFFFF', borderColor: '#E3E1DB' }}
                >
                  <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: '#E3E1DB' }}>
                    <h3 className="font-medium" style={{ color: '#163329' }}>Notifications</h3>
                    {unreadNotificationCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleMarkAllNotificationsRead}
                        className="text-xs"
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm" style={{ color: '#5C635A' }}>No notifications</p>
                    </div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: '#E3E1DB' }}>
                      {notifications.slice(0, 10).map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${!notification.read ? 'bg-blue-50/50' : ''}`}
                          onClick={() => {
                            if (!notification.read) {
                              handleMarkNotificationRead(notification.id);
                            }
                            // Navigate based on notification type
                            if (notification.type === 'product_pending') {
                              setActiveTab('products');
                              setShowNotifications(false);
                            } else if (notification.type === 'kit_pending') {
                              setActiveTab('kits');
                              setShowNotifications(false);
                            } else if (notification.type === 'declaration_pending') {
                              setActiveTab('declarations');
                              setShowNotifications(false);
                            } else if (notification.type === 'course_sale' || notification.type === 'lesson_sale') {
                              setActiveTab('payouts');
                              setShowNotifications(false);
                            } else if (notification.type === 'new_course_created') {
                              setActiveTab('courses');
                              setShowNotifications(false);
                            } else if (notification.type === 'marketplace_order') {
                              setActiveTab('marketplace');
                              setShowNotifications(false);
                            } else if (notification.type === 'support_request') {
                              setActiveTab('support');
                              setShowNotifications(false);
                            } else if (notification.type === 'new_user_signup') {
                              // Navigate to appropriate user tab based on role
                              const userRole = notification.data?.user_role;
                              if (userRole === 'teacher') {
                                setActiveTab('teachers');
                              } else if (userRole === 'seller') {
                                setActiveTab('sellers');
                              } else {
                                setActiveTab('students');
                              }
                              setShowNotifications(false);
                            } else if (notification.action_url) {
                              // Use action_url if provided
                              const tabMatch = notification.action_url.match(/tab=([a-z]+)/);
                              if (tabMatch) {
                                setActiveTab(tabMatch[1]);
                              }
                              setShowNotifications(false);
                            }
                          }}
                        >
                          <div className="flex items-start gap-2">
                            {!notification.read && (
                              <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: '#163329' }}></span>
                            )}
                            <div className={`flex-1 min-w-0 ${!notification.read ? '' : 'pl-4'}`}>
                              <p className="font-medium text-sm" style={{ color: '#163329' }}>
                                {notification.title}
                              </p>
                              <p className="text-xs mt-0.5" style={{ color: '#5C635A' }}>
                                {notification.message}
                              </p>
                              <p className="text-xs mt-1 opacity-60">
                                {new Date(notification.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <button
                              type="button"
                              aria-label="Remove notification"
                              className="flex-shrink-0 p-1 rounded hover:bg-gray-100"
                              style={{ color: '#5C635A' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteNotification(notification.id);
                              }}
                              data-testid={`delete-notification-${notification.id}`}
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Global Commission Setting */}
            <Dialog open={showGlobalCommission} onOpenChange={setShowGlobalCommission}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full"
                
                data-testid="edit-global-commission"
              >
                <Settings className="mr-2 h-4 w-4" />
                Commission: {platformSettings?.default_commission_percentage || 10}%
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>Edit Global Commission Rate</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  This rate applies to all teachers who don't have a custom rate set.
                </p>
                <div>
                  <Label htmlFor="global-commission">Commission Percentage</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="global-commission"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder={platformSettings?.default_commission_percentage?.toString() || "10"}
                      value={newCommission}
                      onChange={(e) => setNewCommission(e.target.value)}
                    />
                    <span className="text-lg font-medium">%</span>
                  </div>
                </div>
                <Button
                  onClick={handleUpdateGlobalCommission}
                  disabled={isUpdating}
                  className="w-full"
                  
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Stats */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#5C635A' }}>Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4" style={{ color: '#163329' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-base md:text-lg font-bold break-words" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Usd value={analytics.summary.total_revenue} />
                  </div>
                  <PriceCaption />
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#5C635A' }}>Course Revenue</CardTitle>
                  <TrendingUp className="h-4 w-4" style={{ color: '#163329' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-base md:text-lg font-bold break-words" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Usd value={(analytics.summary.total_base_revenue || analytics.summary.total_revenue - (analytics.summary.total_kit_revenue || 0))} />
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#5C635A' }}>Base prices only</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <Card style={{ borderColor: '#4CAF50', backgroundColor: '#E8F5E9' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#2E7D32' }}>Kit Revenue</CardTitle>
                  <Package className="h-4 w-4" style={{ color: '#2E7D32' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-base md:text-lg font-bold break-words" style={{ fontFamily: 'Spectral, serif', color: '#2E7D32' }}>
                    <Usd value={(analytics.summary.total_kit_revenue || 0)} />
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#2E7D32' }}>100% platform</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}>
              <Card style={{ borderColor: '#163329', backgroundColor: '#163329' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#E8F5E9' }}>Company Revenue</CardTitle>
                  <DollarSign className="h-4 w-4" style={{ color: '#4CAF50' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-base md:text-lg font-bold break-words" style={{ fontFamily: 'Spectral, serif', color: '#FFFFFF' }}>
                    <Usd value={(analytics.summary.total_platform_earnings || (analytics.summary.total_platform_commission || 0) + (analytics.summary.total_kit_revenue || 0))} />
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#A8D5BA' }}>Commission + Kit</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#5C635A' }}>Commission</CardTitle>
                  <TrendingUp className="h-4 w-4" style={{ color: '#D86B47' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-base md:text-lg font-bold break-words" style={{ fontFamily: 'Spectral, serif', color: '#D86B47' }}>
                    <Usd value={(analytics.summary.total_platform_commission || analytics.summary.total_commission_earned - (analytics.summary.total_kit_revenue || 0))} />
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#5C635A' }}>On base prices</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#5C635A' }}>Teacher Earnings</CardTitle>
                  <Users className="h-4 w-4" style={{ color: '#E2A761' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-base md:text-lg font-bold break-words" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Usd value={analytics.summary.total_teacher_earnings} />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs font-medium" style={{ color: '#5C635A' }}>Transactions</CardTitle>
                  <Percent className="h-4 w-4" style={{ color: '#163329' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {analytics.summary.transaction_count}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Tabs for different sections */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile: Dropdown selector */}
          <div className="block md:hidden mb-4">
            <Label className="mb-2 block font-bold" style={{ color: '#000000', fontSize: '1.2rem' }}>MENU/ OVERVIEW</Label>
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full h-12 px-4 rounded-lg border-2 text-base font-medium"
              style={{ borderColor: '#163329', color: '#163329', backgroundColor: '#FAF9F6' }}
            >
              <option value="payouts">💰 Payouts</option>
              <option value="settings">🏦 Banking Details</option>
              <option value="currencies">💱 Currencies</option>
              <option value="teachers">👨‍🏫 Teachers</option>
              <option value="students">👨‍🎓 Students</option>
              <option value="sellers">🏪 Sellers</option>
              <option value="courses">📚 Courses</option>
              <option value="enrollments">⏰ Enrollments</option>
              <option value="kits">📦 Kits</option>
              <option value="products">🛒 Products</option>
              <option value="declarations">📜 Declarations</option>
              <option value="messages">💬 Messages</option>
              <option value="admins">🛡️ Admins</option>
            </select>
          </div>
          
          {/* Desktop: Scrollable tab list */}
          <div className="hidden md:block overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
            <TabsList className="inline-flex gap-1 w-max h-auto p-1.5 min-w-full" style={{ backgroundColor: '#F0EFEA' }}>
              <TabWithTooltip 
                value="payouts" 
                title="Payouts" 
                description="Manage teacher and seller payment requests"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                Payouts
              </TabWithTooltip>
              <TabWithTooltip 
                value="settings" 
                title="Banking Details" 
                description="Configure platform bank account for receiving payments"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <Building2 className="h-4 w-4 mr-1" />
                Banking Details
              </TabWithTooltip>
              <TabWithTooltip 
                value="currencies" 
                title="Currencies" 
                description="Set exchange rates and supported currencies"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                Currencies
              </TabWithTooltip>
              <TabWithTooltip 
                value="teachers" 
                title="Teachers" 
                description="View and manage registered teachers"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                Teachers
              </TabWithTooltip>
              <TabWithTooltip 
                value="students" 
                title="Students" 
                description="View and manage learner accounts"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                Students
              </TabWithTooltip>
              <TabWithTooltip 
                value="sellers" 
                title="Sellers" 
                description="Manage marketplace sellers and their products"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <Store className="h-4 w-4 mr-1" />
                Sellers
              </TabWithTooltip>
              <TabWithTooltip 
                value="courses" 
                title="Courses" 
                description="Review and approve course submissions"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <GraduationCap className="h-4 w-4 mr-1" />
                Courses
              </TabWithTooltip>
              <TabWithTooltip 
                value="enrollments" 
                title="Enrollments" 
                description="Manage course enrollment requests"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <Clock className="h-4 w-4 mr-1" />
                Enrollments
              </TabWithTooltip>
              <TabWithTooltip 
                value="kits" 
                title="Kits" 
                description="Review learning kit submissions from teachers"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <Package className="h-4 w-4 mr-1" />
                Kits
              </TabWithTooltip>
              <TabWithTooltip 
                value="products" 
                title="Products" 
                description="Approve marketplace product listings"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <ShoppingBag className="h-4 w-4 mr-1" />
                Products
              </TabWithTooltip>
              <TabWithTooltip 
                value="declarations" 
                title="Declarations" 
                description="Approve course acknowledgment declarations"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <FileText className="h-4 w-4 mr-1" />
                Declarations
                {pendingDeclarations.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">{pendingDeclarations.length}</span>
                )}
              </TabWithTooltip>
              <TabWithTooltip 
                value="messages" 
                title="Messages" 
                description="View and reply to user support messages"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                Messages
              </TabWithTooltip>
              <TabWithTooltip 
                value="admins" 
                title="Admins" 
                description="Manage administrator accounts and permissions"
                className="px-3 py-2 text-sm whitespace-nowrap"
              >
                <Shield className="h-4 w-4 mr-1" />
                Admins
              </TabWithTooltip>
            </TabsList>
          </div>

          {/* Settings Tab - Platform Bank Details */}
          <TabsContent value="settings">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Building2 className="h-5 w-5" />
                    Platform Bank Account
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                    This is where student payments will be deposited when they purchase courses.
                  </p>
                  
                  {adminBankDetails?.has_bank_details ? (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-5 w-5" style={{ color: '#2E7D32' }} />
                          <span className="font-medium" style={{ color: '#163329' }}>Bank Account Configured</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                          <div>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Bank Name</p>
                            <p className="font-medium" style={{ color: '#163329' }}>{adminBankDetails.bank_name}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Account Holder</p>
                            <p className="font-medium" style={{ color: '#163329' }}>{adminBankDetails.account_holder_name}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Account Number</p>
                            <p className="font-medium font-mono" style={{ color: '#163329' }}>{'*'.repeat((adminBankDetails.account_number_length || 8) - 4)}{adminBankDetails.account_number_last4}</p>
                          </div>
                          {adminBankDetails.routing_number && (
                            <div>
                              <p className="text-sm" style={{ color: '#5C635A' }}>Routing Number</p>
                              <p className="font-medium font-mono" style={{ color: '#163329' }}>{adminBankDetails.routing_number}</p>
                            </div>
                          )}
                          {adminBankDetails.swift_code && (
                            <div>
                              <p className="text-sm" style={{ color: '#5C635A' }}>SWIFT Code</p>
                              <p className="font-medium font-mono" style={{ color: '#163329' }}>{adminBankDetails.swift_code}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => setShowAdminBankForm(true)}
                        variant="outline"
                        className="rounded-full"
                        style={{ borderColor: '#163329', color: '#163329' }}
                      >
                        Update Bank Details
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFF8E7' }}>
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5" style={{ color: '#D86B47' }} />
                          <span className="font-medium" style={{ color: '#163329' }}>No bank account configured</span>
                        </div>
                        <p className="text-sm mt-2" style={{ color: '#5C635A' }}>
                          Please add your bank details to receive student payments.
                        </p>
                      </div>
                      
                      <Button
                        onClick={() => setShowAdminBankForm(true)}
                        className="rounded-full"
                        
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        Add Bank Details
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              <PlatformSignatureCard />
            </motion.div>
          </TabsContent>

          {/* Currencies Tab */}
          <TabsContent value="currencies">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Globe className="h-5 w-5" />
                    Currency Exchange Rates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                    Manage exchange rates for course pricing. Live rates are fetched automatically, but you can set manual overrides.
                    Prices are stored in USD and converted for display based on user's selected currency.
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                          <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Currency</th>
                          <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Symbol</th>
                          <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Live Rate</th>
                          <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Manual Rate</th>
                          <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Effective</th>
                          <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Active</th>
                          <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currencies.map((currency) => (
                          <tr key={currency.code} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium" style={{ color: '#163329' }}>{currency.code}</p>
                                <p className="text-sm" style={{ color: '#5C635A' }}>{currency.name}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-lg" style={{ color: '#163329' }}>{currency.symbol}</td>
                            <td className="py-3 px-4 font-mono" style={{ color: '#5C635A' }}>
                              {currency.live_rate?.toFixed(4) || '-'}
                            </td>
                            <td className="py-3 px-4">
                              {editingCurrency === currency.code ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.0001"
                                    value={editCurrencyRate}
                                    onChange={(e) => setEditCurrencyRate(e.target.value)}
                                    className="w-24"
                                    placeholder={currency.live_rate?.toString()}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateCurrencyRate(currency.code)}
                                    disabled={isUpdating}
                                    
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingCurrency(null);
                                      setEditCurrencyRate('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <span className="font-mono" style={{ color: currency.is_manual ? '#D86B47' : '#5C635A' }}>
                                  {currency.manual_rate?.toFixed(4) || '-'}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 font-mono font-medium" style={{ color: '#163329' }}>
                              {currency.effective_rate?.toFixed(4)}
                            </td>
                            <td className="py-3 px-4">
                              <Switch
                                checked={currency.is_active}
                                onCheckedChange={() => handleToggleCurrency(currency)}
                                disabled={currency.code === 'USD' || isUpdating}
                              />
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                {editingCurrency !== currency.code && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingCurrency(currency.code);
                                      setEditCurrencyRate(currency.effective_rate?.toString() || '');
                                    }}
                                    disabled={currency.code === 'USD'}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                )}
                                {currency.is_manual && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResetToLiveRate(currency.code)}
                                    disabled={isUpdating}
                                    title="Reset to live rate"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      <strong>Note:</strong> Exchange rates are fetched from live markets and cached for 1 hour.
                      Manual rates (shown in orange) override live rates. USD is always the base currency and cannot be modified.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Teachers Tab */}
          <TabsContent value="teachers">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <div className="flex items-center justify-between">
                      <span>All Teachers</span>
                      <span className="text-sm font-normal" style={{ color: '#5C635A' }}>
                        {teachers.length} total
                      </span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {teachers.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No teachers registered yet</p>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {teachers.map((teacher) => (
                        <div
                          key={teacher.id}
                          onClick={() => setSelectedTeacher(teacher)}
                          className="p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md"
                          style={{ 
                            borderColor: '#E3E1DB', 
                            backgroundColor: teacher.is_active !== false ? '#FFFFFF' : '#FFF5F5'
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                                style={{ backgroundColor: '#163329' }}
                              >
                                {teacher.name?.charAt(0)?.toUpperCase() || 'T'}
                              </div>
                              <div>
                                <p className="font-medium" style={{ color: '#163329' }}>{teacher.name}</p>
                                <p className="text-xs" style={{ color: '#5C635A' }}>{teacher.email}</p>
                              </div>
                            </div>
                            <ChevronRight className="h-5 w-5" style={{ color: '#5C635A' }} />
                          </div>
                          
                          <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: '#E3E1DB' }}>
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="text-xs" style={{ color: '#5C635A' }}>Sales</p>
                                <p className="font-medium text-sm" style={{ color: '#163329' }}><Usd value={teacher.total_sales} /></p>
                              </div>
                              <div>
                                <p className="text-xs" style={{ color: '#5C635A' }}>Courses</p>
                                <p className="font-medium text-sm" style={{ color: '#163329' }}>{teacher.course_count || 0}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {teacher.is_active !== false ? (
                                <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Teacher Detail Sheet */}
            <Sheet open={!!selectedTeacher} onOpenChange={(open) => !open && setSelectedTeacher(null)}>
              <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
                {selectedTeacher && (
                  <>
                    <SheetHeader className="pb-4 border-b" style={{ borderColor: '#E3E1DB' }}>
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-medium"
                          style={{ backgroundColor: '#163329' }}
                        >
                          {selectedTeacher.name?.charAt(0)?.toUpperCase() || 'T'}
                        </div>
                        <div>
                          <SheetTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                            {selectedTeacher.name}
                          </SheetTitle>
                          <p className="text-sm" style={{ color: '#5C635A' }}>{selectedTeacher.email}</p>
                          {selectedTeacher.is_active !== false ? (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                              Active
                            </span>
                          ) : (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                              Deactivated
                            </span>
                          )}
                        </div>
                      </div>
                    </SheetHeader>

                    <div className="py-6 space-y-6">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                          <p className="text-xs" style={{ color: '#5C635A' }}>Total Sales</p>
                          <p className="text-xl font-bold" style={{ color: '#163329' }}>
                            <Usd value={selectedTeacher.total_sales} />
                          </p>
                        </div>
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                          <p className="text-xs" style={{ color: '#5C635A' }}>Courses</p>
                          <p className="text-xl font-bold" style={{ color: '#163329' }}>
                            {selectedTeacher.course_count || 0}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFF8E7' }}>
                          <p className="text-xs" style={{ color: '#5C635A' }}>Commission Rate</p>
                          <p className="text-xl font-bold" style={{ color: '#163329' }}>
                            {selectedTeacher.effective_commission_percentage?.toFixed(1) || '0'}%
                          </p>
                          {selectedTeacher.custom_commission_rate !== null && (
                            <p className="text-xs mt-1" style={{ color: '#D86B47' }}>Custom rate</p>
                          )}
                        </div>
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                          <p className="text-xs" style={{ color: '#5C635A' }}>Bank Details</p>
                          {selectedTeacher.has_bank_details ? (
                            <div className="flex items-center gap-1 mt-1">
                              {selectedTeacher.bank_verified ? (
                                <CheckCircle className="h-4 w-4" style={{ color: '#2E7D32' }} />
                              ) : (
                                <AlertCircle className="h-4 w-4" style={{ color: '#D86B47' }} />
                              )}
                              <span className="text-sm font-medium" style={{ color: '#163329' }}>
                                {'*'.repeat((selectedTeacher.account_number_length || 8) - 4)}{selectedTeacher.account_number_last4}
                              </span>
                            </div>
                          ) : (
                            <p className="text-sm" style={{ color: '#5C635A' }}>Not set</p>
                          )}
                        </div>
                      </div>

                      {/* Actions Section */}
                      <div className="space-y-3">
                        <p className="text-sm font-medium" style={{ color: '#163329' }}>Actions</p>
                        
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            className="w-full justify-center"
                            onClick={() => handleImpersonate(selectedTeacher.id)}
                          >
                            <LogIn className="h-4 w-4 mr-2" />
                            Login as Teacher
                          </Button>
                          
                          <Button
                            variant="outline"
                            className="w-full justify-center"
                            onClick={() => {
                              setSelectedChatUser({ id: selectedTeacher.id, name: selectedTeacher.name, role: 'teacher' });
                              setActiveTab('messages');
                              handleSelectChatUser({ id: selectedTeacher.id, name: selectedTeacher.name, role: 'teacher' });
                              setSelectedTeacher(null);
                            }}
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Send Message
                          </Button>
                          
                          {selectedTeacher.has_bank_details && (
                            <Button
                              variant="outline"
                              className="w-full justify-center"
                              onClick={() => handleViewBankDetails(selectedTeacher.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Bank Details
                            </Button>
                          )}
                          
                          <Dialog open={showTeacherCommission === selectedTeacher.id} onOpenChange={(open) => {
                            setShowTeacherCommission(open ? selectedTeacher.id : null);
                            if (!open) setNewCommission('');
                          }}>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="w-full justify-center">
                                <Percent className="h-4 w-4 mr-2" />
                                Set Commission
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                                  Commission for {selectedTeacher.name}
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 pt-4">
                                <div>
                                  <Label>Commission Percentage</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      placeholder={selectedTeacher.effective_commission_percentage?.toString()}
                                      value={newCommission}
                                      onChange={(e) => setNewCommission(e.target.value)}
                                    />
                                    <span className="text-lg font-medium">%</span>
                                  </div>
                                </div>
                                <p className="text-sm" style={{ color: '#5C635A' }}>
                                  Current: {selectedTeacher.effective_commission_percentage?.toFixed(1)}%
                                  {selectedTeacher.custom_commission_rate !== null ? ' (custom)' : ' (platform default)'}
                                </p>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => {
                                      handleUpdateTeacherCommission(selectedTeacher.id);
                                      // Update local state
                                      setSelectedTeacher({
                                        ...selectedTeacher,
                                        effective_commission_percentage: parseFloat(newCommission) || selectedTeacher.effective_commission_percentage,
                                        custom_commission_rate: parseFloat(newCommission) || null
                                      });
                                    }}
                                    disabled={isUpdating}
                                    className="flex-1"
                                    style={{ backgroundColor: '#163329' }}
                                  >
                                    {isUpdating ? 'Saving...' : 'Save'}
                                  </Button>
                                  {selectedTeacher.custom_commission_rate !== null && (
                                    <Button
                                      variant="outline"
                                      onClick={async () => {
                                        try {
                                          await api.delete(`/admin/teachers/${selectedTeacher.id}/commission`);
                                          toast.success('Reset to platform default');
                                          fetchData();
                                          setShowTeacherCommission(null);
                                        } catch (error) {
                                          toast.error('Failed to reset');
                                        }
                                      }}
                                    >
                                      <RotateCcw className="h-4 w-4 mr-1" />
                                      Reset
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>

                        {/* Status & Delete Actions */}
                        <div className="pt-4 mt-4 border-t space-y-2" style={{ borderColor: '#E3E1DB' }}>
                          <Button
                            variant={selectedTeacher.is_active !== false ? "outline" : "default"}
                            className="w-full justify-start"
                            onClick={() => {
                              handleToggleUserStatus(selectedTeacher.id, selectedTeacher.is_active !== false);
                              setSelectedTeacher({
                                ...selectedTeacher,
                                is_active: selectedTeacher.is_active === false ? true : false
                              });
                            }}
                            style={selectedTeacher.is_active === false ? { backgroundColor: '#163329' } : {}}
                          >
                            {selectedTeacher.is_active !== false ? (
                              <>
                                <UserX className="h-4 w-4 mr-2" />
                                Deactivate Account
                              </>
                            ) : (
                              <>
                                <UserCheck className="h-4 w-4 mr-2" />
                                Reactivate Account
                              </>
                            )}
                          </Button>
                          
                          <Button
                            variant="outline"
                            className="w-full justify-start text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => {
                              handleDeleteUser(selectedTeacher.id, selectedTeacher.name, 'teacher');
                              setSelectedTeacher(null);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Permanently Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </SheetContent>
            </Sheet>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    Teacher Payouts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {payouts.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No teachers with earnings yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Teacher</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Total Earnings</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Paid Out</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Pending</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Bank Details</th>
                            <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.map((payout) => (
                            <tr key={payout.teacher_id} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <td className="py-3 px-4">
                                <p className="font-medium" style={{ color: '#163329' }}>{payout.teacher_name}</p>
                                <p className="text-sm" style={{ color: '#5C635A' }}>{payout.teacher_email}</p>
                              </td>
                              <td className="py-3 px-4" style={{ color: '#163329' }}><Usd value={payout.total_earnings} /></td>
                              <td className="py-3 px-4" style={{ color: '#2E7D32' }}><Usd value={payout.total_paid} /></td>
                              <td className="py-3 px-4">
                                <span className="font-bold" style={{ color: payout.pending_amount > 0 ? '#D86B47' : '#163329' }}>
                                  <Usd value={payout.pending_amount} />
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                {payout.has_bank_details ? (
                                  <div>
                                    <p className="text-sm" style={{ color: '#163329' }}>{payout.bank_name}</p>
                                    <p className="text-sm flex items-center gap-1" style={{ color: '#5C635A' }}>
                                      {'*'.repeat((payout.account_number_length || 8) - 4)}{payout.account_number_last4}
                                      {payout.bank_verified && <CheckCircle className="h-3 w-3" style={{ color: '#2E7D32' }} />}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
                                    Not provided
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  {payout.has_bank_details && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleViewBankDetails(payout.teacher_id)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {payout.has_bank_details && payout.pending_amount > 0 && (
                                    <Dialog open={showPayoutDialog === payout.teacher_id} onOpenChange={(open) => {
                                      setShowPayoutDialog(open ? payout.teacher_id : null);
                                      if (open) setPayoutAmount(payout.pending_amount.toString());
                                      if (!open) {
                                        setPayoutAmount('');
                                        setPayoutNotes('');
                                      }
                                    }}>
                                      <DialogTrigger asChild>
                                        <Button
                                          size="sm"
                                          className="rounded-full"
                                          
                                        >
                                          <CreditCard className="h-4 w-4 mr-1" />
                                          Pay
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent>
                                        <DialogHeader>
                                          <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                                            Record Payout to {payout.teacher_name}
                                          </DialogTitle>
                                        </DialogHeader>
                                        <div className="space-y-4 pt-4">
                                          <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                                            <p className="text-sm" style={{ color: '#5C635A' }}>Bank: {payout.bank_name}</p>
                                            <p className="text-sm" style={{ color: '#5C635A' }}>Account: {'*'.repeat((payout.account_number_length || 8) - 4)}{payout.account_number_last4}</p>
                                            <p className="text-sm font-medium mt-2" style={{ color: '#163329' }}>
                                              Pending: <Usd value={payout.pending_amount} />
                                            </p>
                                          </div>
                                          <div>
                                            <Label>Payout Amount</Label>
                                            <div className="flex items-center gap-2 mt-1">
                                              <span className="text-lg">$</span>
                                              <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={payoutAmount}
                                                onChange={(e) => setPayoutAmount(e.target.value)}
                                              />
                                            </div>
                                          </div>
                                          <div>
                                            <Label>Notes (optional)</Label>
                                            <Input
                                              placeholder="e.g., Bank transfer ref #12345"
                                              value={payoutNotes}
                                              onChange={(e) => setPayoutNotes(e.target.value)}
                                            />
                                          </div>
                                          <Button
                                            onClick={() => handleCreatePayout(payout.teacher_id)}
                                            disabled={isUpdating}
                                            className="w-full"
                                            
                                          >
                                            {isUpdating ? 'Recording...' : 'Mark as Paid'}
                                          </Button>
                                        </div>
                                      </DialogContent>
                                    </Dialog>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seller Payouts Section */}
              <Card className="mt-6" style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <div className="flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5" />
                      Seller Payouts (Marketplace)
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sellerPayouts.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No sellers with marketplace earnings yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Seller</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Total Sales</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Platform Fee</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Net Earnings</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Paid Out</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Pending</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Bank Details</th>
                            <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sellerPayouts.map((payout) => (
                            <tr key={payout.seller_id} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <td className="py-3 px-4">
                                <p className="font-medium" style={{ color: '#163329' }}>{payout.seller_name}</p>
                                <p className="text-sm" style={{ color: '#5C635A' }}>{payout.seller_email}</p>
                                <p className="text-xs" style={{ color: '#888' }}>{payout.order_count} orders • {payout.commission_percentage}% fee</p>
                              </td>
                              <td className="py-3 px-4" style={{ color: '#163329' }}><Usd value={payout.total_sales} /></td>
                              <td className="py-3 px-4" style={{ color: '#D86B47' }}><Usd value={payout.platform_fee} /></td>
                              <td className="py-3 px-4" style={{ color: '#163329' }}><Usd value={payout.total_earnings} /></td>
                              <td className="py-3 px-4" style={{ color: '#2E7D32' }}><Usd value={payout.total_paid} /></td>
                              <td className="py-3 px-4">
                                <span className="font-bold" style={{ color: payout.pending_amount > 0 ? '#D86B47' : '#163329' }}>
                                  <Usd value={payout.pending_amount} />
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                {payout.has_bank_details ? (
                                  <div>
                                    <p className="text-sm" style={{ color: '#163329' }}>{payout.bank_name}</p>
                                    <p className="text-sm flex items-center gap-1" style={{ color: '#5C635A' }}>
                                      {'*'.repeat((payout.account_number_length || 8) - 4)}{payout.account_number_last4}
                                      {payout.bank_verified && <CheckCircle className="h-3 w-3" style={{ color: '#2E7D32' }} />}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
                                    Not provided
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  {payout.has_bank_details && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleViewSellerBankDetails(payout.seller_id)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {payout.has_bank_details && payout.pending_amount > 0 && (
                                    <Dialog open={showSellerPayoutDialog === payout.seller_id} onOpenChange={(open) => {
                                      setShowSellerPayoutDialog(open ? payout.seller_id : null);
                                      if (open) setPayoutAmount(payout.pending_amount.toString());
                                      if (!open) {
                                        setPayoutAmount('');
                                        setPayoutNotes('');
                                      }
                                    }}>
                                      <DialogTrigger asChild>
                                        <Button
                                          size="sm"
                                          className="rounded-full"
                                        >
                                          <CreditCard className="h-4 w-4 mr-1" />
                                          Pay
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent>
                                        <DialogHeader>
                                          <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                                            Record Payout to {payout.seller_name}
                                          </DialogTitle>
                                        </DialogHeader>
                                        <div className="space-y-4 pt-4">
                                          <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                                            <p className="text-sm" style={{ color: '#5C635A' }}>Bank: {payout.bank_name}</p>
                                            <p className="text-sm" style={{ color: '#5C635A' }}>Account: {'*'.repeat((payout.account_number_length || 8) - 4)}{payout.account_number_last4}</p>
                                            <p className="text-sm font-medium mt-2" style={{ color: '#163329' }}>
                                              Pending: <Usd value={payout.pending_amount} />
                                            </p>
                                          </div>
                                          <div>
                                            <Label>Payout Amount</Label>
                                            <div className="flex items-center gap-2 mt-1">
                                              <span className="text-lg">$</span>
                                              <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={payoutAmount}
                                                onChange={(e) => setPayoutAmount(e.target.value)}
                                              />
                                            </div>
                                          </div>
                                          <div>
                                            <Label>Notes (optional)</Label>
                                            <Input
                                              placeholder="e.g., Bank transfer ref #12345"
                                              value={payoutNotes}
                                              onChange={(e) => setPayoutNotes(e.target.value)}
                                            />
                                          </div>
                                          <Button
                                            onClick={() => handleCreateSellerPayout(payout.seller_id)}
                                            disabled={isUpdating}
                                            className="w-full"
                                          >
                                            {isUpdating ? 'Recording...' : 'Mark as Paid'}
                                          </Button>
                                        </div>
                                      </DialogContent>
                                    </Dialog>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seller Bank Details Dialog */}
              <Dialog open={!!showSellerBankDetails} onOpenChange={(open) => !open && setShowSellerBankDetails(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                      Seller Bank Details
                    </DialogTitle>
                  </DialogHeader>
                  {sellerBankDetailsData && (
                    <div className="space-y-4 pt-4">
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                        <div className="grid gap-3">
                          <div>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Seller</p>
                            <p className="font-medium" style={{ color: '#163329' }}>{sellerBankDetailsData.seller_name}</p>
                            <p className="text-sm" style={{ color: '#5C635A' }}>{sellerBankDetailsData.seller_email}</p>
                          </div>
                          <div className="border-t pt-3" style={{ borderColor: '#E3E1DB' }}>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Bank Name</p>
                            <p className="font-medium" style={{ color: '#163329' }}>{sellerBankDetailsData.bank_name}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Account Holder</p>
                            <p className="font-medium" style={{ color: '#163329' }}>{sellerBankDetailsData.account_holder_name}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#5C635A' }}>Account Number</p>
                            <p className="font-medium font-mono" style={{ color: '#163329' }}>{sellerBankDetailsData.account_number}</p>
                          </div>
                          {sellerBankDetailsData.routing_number && (
                            <div>
                              <p className="text-sm" style={{ color: '#5C635A' }}>Routing Number</p>
                              <p className="font-medium font-mono" style={{ color: '#163329' }}>{sellerBankDetailsData.routing_number}</p>
                            </div>
                          )}
                          {sellerBankDetailsData.swift_code && (
                            <div>
                              <p className="text-sm" style={{ color: '#5C635A' }}>SWIFT Code</p>
                              <p className="font-medium font-mono" style={{ color: '#163329' }}>{sellerBankDetailsData.swift_code}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </motion.div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <GraduationCap className="h-5 w-5" />
                    All Students
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {students.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No students registered yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Student</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Courses</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Total Spent</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Status</th>
                            <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((student) => (
                            <tr key={student.id} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <td className="py-3 px-4">
                                <p className="font-medium" style={{ color: '#163329' }}>{student.name}</p>
                                <p className="text-sm" style={{ color: '#5C635A' }}>{student.email}</p>
                                {student.phone && <p className="text-xs" style={{ color: '#5C635A' }}>{student.phone}</p>}
                              </td>
                              <td className="py-3 px-4" style={{ color: '#163329' }}>{student.enrollment_count}</td>
                              <td className="py-3 px-4" style={{ color: '#163329' }}><Usd value={student.total_spent} /></td>
                              <td className="py-3 px-4">
                                {student.is_active ? (
                                  <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                                    Active
                                  </span>
                                ) : (
                                  <div>
                                    <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                                      Deactivated
                                    </span>
                                    {student.deactivation_reason && (
                                      <p className="text-xs mt-1" style={{ color: '#5C635A' }}>{student.deactivation_reason}</p>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleImpersonate(student.id)}
                                    title="Login as this student"
                                  >
                                    <LogIn className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedChatUser({ id: student.id, name: student.name, role: 'student' });
                                      setActiveTab('messages');
                                      handleSelectChatUser({ id: student.id, name: student.name, role: 'student' });
                                    }}
                                    title="Message this student"
                                  >
                                    <MessageCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={student.is_active ? "destructive" : "default"}
                                    onClick={() => handleToggleUserStatus(student.id, student.is_active)}
                                    title={student.is_active ? 'Deactivate account' : 'Activate account'}
                                  >
                                    {student.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => handleDeleteUser(student.id, student.name, 'student')}
                                    title="Permanently delete account"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Sellers Tab */}
          <TabsContent value="sellers">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Store className="h-5 w-5" />
                    All Sellers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sellers.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No sellers registered yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Seller</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Products</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Status</th>
                            <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sellers.map((seller) => (
                            <tr key={seller.id} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <td className="py-3 px-4">
                                <p className="font-medium" style={{ color: '#163329' }}>{seller.name}</p>
                                <p className="text-sm" style={{ color: '#5C635A' }}>{seller.email}</p>
                                {seller.phone && <p className="text-xs" style={{ color: '#5C635A' }}>{seller.phone}</p>}
                              </td>
                              <td className="py-3 px-4">
                                <p style={{ color: '#163329' }}>{seller.approved_products} / {seller.total_products}</p>
                                <p className="text-xs" style={{ color: '#5C635A' }}>Approved / Total</p>
                              </td>
                              <td className="py-3 px-4">
                                {seller.is_active ? (
                                  <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                                    Active
                                  </span>
                                ) : (
                                  <div>
                                    <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                                      Deactivated
                                    </span>
                                    {seller.deactivation_reason && (
                                      <p className="text-xs mt-1" style={{ color: '#5C635A' }}>{seller.deactivation_reason}</p>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleImpersonate(seller.id)}
                                    title="Login as this seller"
                                  >
                                    <LogIn className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedChatUser({ id: seller.id, name: seller.name, role: 'seller' });
                                      setActiveTab('messages');
                                      handleSelectChatUser({ id: seller.id, name: seller.name, role: 'seller' });
                                    }}
                                    title="Message this seller"
                                  >
                                    <MessageCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={seller.is_active ? "destructive" : "default"}
                                    onClick={() => handleToggleUserStatus(seller.id, seller.is_active)}
                                    title={seller.is_active ? 'Deactivate account' : 'Activate account'}
                                  >
                                    {seller.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => handleDeleteUser(seller.id, seller.name, 'seller')}
                                    title="Permanently delete account"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {allCourses.length > 1 && <CourseOrderCard courses={allCourses} onSaved={fetchAllCourses} />}
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <GraduationCap className="h-5 w-5" />
                    All Courses - Price Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allCourses.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No courses found</p>
                  ) : (
                    <div className="space-y-4">
                      {allCourses.map((course) => (
                        <div 
                          key={course.id} 
                          className="p-4 rounded-lg border"
                          style={{ 
                            borderColor: course.kit?.status === 'pending' ? '#FFA000' : '#E3E1DB',
                            backgroundColor: course.kit?.status === 'pending' ? '#FFF8E1' : '#FFFFFF'
                          }}
                        >
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <div className="flex-1">
                              <h4 className="font-semibold" style={{ color: '#163329' }}>{course.title}</h4>
                              <p className="text-sm" style={{ color: '#5C635A' }}>
                                Teacher: {course.teacher_name} ({course.teacher_email})
                              </p>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                                <span style={{ color: '#163329' }}>
                                  <strong>Base Price:</strong> <Usd value={(course.base_price || course.price || 0)} />
                                </span>
                                {course.has_kit && (
                                  <span style={{ color: '#D86B47' }}>
                                    <strong>Kit Price:</strong> <Usd value={(course.kit_price || 0)} />
                                  </span>
                                )}
                                <span className="font-bold" style={{ color: '#163329' }}>
                                  <strong>Total Price:</strong> <Usd value={(course.price || 0)} />
                                </span>
                                <span style={{ color: '#5C635A' }}>
                                  Enrollments: {course.enrollment_count}
                                </span>
                              </div>
                              {course.kit && (
                                <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#F0EFEA' }}>
                                  <p className="text-xs font-medium" style={{ color: '#163329' }}>
                                    <Package className="inline h-3 w-3 mr-1" />
                                    Kit Status: 
                                    <span className={`ml-1 px-2 py-0.5 rounded text-white ${
                                      course.kit.status === 'pending' ? 'bg-yellow-500' : 
                                      course.kit.status === 'verified' ? 'bg-green-500' : 
                                      course.kit.status === 'ordered' ? 'bg-blue-500' : 'bg-gray-500'
                                    }`}>
                                      {course.kit.status}
                                    </span>
                                  </p>
                                  <div className="mt-1 text-xs" style={{ color: '#5C635A' }}>
                                    <strong>Kit Items:</strong>
                                    <ul className="ml-4 list-disc">
                                      {course.kit.items?.map((item, idx) => (
                                        <li key={idx}>
                                          {item.name} x{item.quantity} @ <Usd value={(item.unit_price || 0)} /> = <Usd value={(item.item_total || 0)} />
                                        </li>
                                      ))}
                                    </ul>
                                    <p className="mt-1 font-medium">Kit Total: <Usd value={(course.kit.total_kit_price || 0)} /></p>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 sm:ml-3 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDuplicatingCourse(course)}
                              style={{ borderColor: '#163329', color: '#163329' }}
                              data-testid={`duplicate-course-btn-${course.id}`}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Duplicate
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedCourse(course);
                                setCourseEditPrice(course.price?.toString() || '0');
                                setCourseEditNotes('');
                              }}
                              style={{ backgroundColor: '#163329' }}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit Price
                            </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <DuplicateCourseDialog course={duplicatingCourse} onClose={() => setDuplicatingCourse(null)} onDuplicated={fetchAllCourses} />
            </motion.div>

            {/* Edit Course Price Dialog */}
            <Dialog open={!!selectedCourse} onOpenChange={(open) => !open && setSelectedCourse(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <DollarSign className="inline h-5 w-5 mr-2" />
                    Adjust Course Price
                  </DialogTitle>
                </DialogHeader>
                {selectedCourse && (
                  <div className="space-y-4 pt-4">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                      <p className="font-medium" style={{ color: '#163329' }}>{selectedCourse.title}</p>
                      <p className="text-sm" style={{ color: '#5C635A' }}>Teacher: {selectedCourse.teacher_name}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label style={{ color: '#5C635A' }}>Base Price</Label>
                        <p className="font-medium" style={{ color: '#163329' }}>
                          <Usd value={(selectedCourse.base_price || selectedCourse.price || 0)} />
                        </p>
                      </div>
                      {selectedCourse.has_kit && (
                        <div>
                          <Label style={{ color: '#5C635A' }}>Kit Price</Label>
                          <p className="font-medium" style={{ color: '#D86B47' }}>
                            <Usd value={(selectedCourse.kit_price || 0)} />
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="new-price">New Total Price ($)</Label>
                      <Input
                        id="new-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={courseEditPrice}
                        onChange={(e) => setCourseEditPrice(e.target.value)}
                        placeholder="Enter new price"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="price-notes">Notes (optional)</Label>
                      <Textarea
                        id="price-notes"
                        value={courseEditNotes}
                        onChange={(e) => setCourseEditNotes(e.target.value)}
                        placeholder="Reason for price adjustment..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}>
                      <p className="text-sm" style={{ color: '#2E7D32' }}>
                        <CheckCircle className="inline h-4 w-4 mr-1" />
                        The teacher will be notified about this price adjustment and can proceed with tutorials.
                      </p>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedCourse(null)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleUpdateCoursePrice(selectedCourse.id)}
                        disabled={isUpdating}
                        className="flex-1"
                        style={{ backgroundColor: '#163329' }}
                      >
                        {isUpdating ? 'Updating...' : 'Update Price'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Clock className="h-5 w-5" />
                    Course Enrollments & Validity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                    Manage course enrollment periods. All courses have a 3-month validity by default. You can extend access for any student.
                  </p>
                  
                  {enrollments.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No enrollments found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Student</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Course</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Enrolled</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Expires</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Progress</th>
                            <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: '#5C635A' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enrollments.map((enrollment) => (
                            <tr key={enrollment.enrollment_id} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <td className="py-3 px-4">
                                <p className="font-medium" style={{ color: '#163329' }}>{enrollment.student_name}</p>
                                <p className="text-sm" style={{ color: '#5C635A' }}>{enrollment.student_email}</p>
                              </td>
                              <td className="py-3 px-4" style={{ color: '#163329' }}>{enrollment.course_title}</td>
                              <td className="py-3 px-4 text-sm" style={{ color: '#5C635A' }}>
                                {enrollment.enrolled_at ? new Date(enrollment.enrolled_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="py-3 px-4 text-sm" style={{ color: '#5C635A' }}>
                                {enrollment.expires_at ? new Date(enrollment.expires_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="py-3 px-4">
                                {enrollment.is_expired ? (
                                  <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                                    Expired
                                  </span>
                                ) : (
                                  <div>
                                    <span 
                                      className="px-2 py-1 text-xs rounded-full"
                                      style={{ 
                                        backgroundColor: enrollment.days_remaining <= 7 ? '#FEF3C7' : '#E8F5E9',
                                        color: enrollment.days_remaining <= 7 ? '#D97706' : '#2E7D32'
                                      }}
                                    >
                                      {enrollment.days_remaining <= 0 
                                        ? 'Expires today' 
                                        : `${enrollment.days_remaining} days left`}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 bg-gray-200 rounded-full">
                                    <div 
                                      className="h-full rounded-full" 
                                      style={{ width: `${enrollment.progress || 0}%`, backgroundColor: '#163329' }}
                                    />
                                  </div>
                                  <span className="text-xs" style={{ color: '#5C635A' }}>{Math.round(enrollment.progress || 0)}%</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Dialog 
                                  open={showExtendDialog === enrollment.enrollment_id} 
                                  onOpenChange={(open) => {
                                    setShowExtendDialog(open ? enrollment.enrollment_id : null);
                                    if (!open) setExtensionDays('30');
                                  }}
                                >
                                  <DialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      className="rounded-full"
                                      style={{ backgroundColor: enrollment.is_expired ? '#D86B47' : '#163329', color: '#FAF9F6' }}
                                      title="Extend enrollment period"
                                    >
                                      <CalendarPlus className="h-4 w-4 mr-1" />
                                      Extend
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                                        Extend Enrollment Period
                                      </DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 pt-4">
                                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                                        <p className="text-sm" style={{ color: '#5C635A' }}>
                                          <strong>Student:</strong> {enrollment.student_name}
                                        </p>
                                        <p className="text-sm" style={{ color: '#5C635A' }}>
                                          <strong>Course:</strong> {enrollment.course_title}
                                        </p>
                                        <p className="text-sm mt-2" style={{ color: enrollment.is_expired ? '#DC2626' : '#5C635A' }}>
                                          <strong>Current Status:</strong> {enrollment.is_expired ? 'Expired' : `${enrollment.days_remaining} days remaining`}
                                        </p>
                                      </div>
                                      
                                      <div>
                                        <Label>Extension Period (days)</Label>
                                        <div className="flex items-center gap-2 mt-1">
                                          <Input
                                            type="number"
                                            min="1"
                                            max="365"
                                            value={extensionDays}
                                            onChange={(e) => setExtensionDays(e.target.value)}
                                            placeholder="30"
                                          />
                                          <span className="text-sm" style={{ color: '#5C635A' }}>days</span>
                                        </div>
                                        <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                                          Common: 30 days (1 month), 90 days (3 months), 180 days (6 months)
                                        </p>
                                      </div>
                                      
                                      <div className="flex gap-2">
                                        {[30, 60, 90].map((d) => (
                                          <Button
                                            key={d}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setExtensionDays(d.toString())}
                                            className={extensionDays === d.toString() ? 'ring-2 ring-green-500' : ''}
                                          >
                                            {d} days
                                          </Button>
                                        ))}
                                      </div>
                                      
                                      <Button
                                        onClick={() => handleExtendEnrollment(enrollment.enrollment_id)}
                                        disabled={isUpdating}
                                        className="w-full"
                                        
                                      >
                                        {isUpdating ? 'Extending...' : `Extend by ${extensionDays} days`}
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Kits Tab */}
          <TabsContent value="kits">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Package className="h-5 w-5" />
                    Course Kit Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                    View and manage kit requests from teachers. Kits are physical materials needed for workshop courses that Insight Evolution supplies on behalf of teachers.
                  </p>
                  
                  {courseKits.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No kit requests yet</p>
                  ) : (
                    <div className="space-y-4">
                      {courseKits.map((kit) => (
                        <div 
                          key={kit.id} 
                          className="p-4 rounded-lg border"
                          style={{ 
                            borderColor: kit.status === 'pending' ? '#FFA000' : '#E3E1DB',
                            backgroundColor: kit.status === 'pending' ? '#FFF8E1' : 'white'
                          }}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold" style={{ color: '#163329' }}>{kit.course_title}</h4>
                              <p className="text-sm" style={{ color: '#5C635A' }}>
                                Teacher: {kit.teacher_name} ({kit.teacher_email})
                              </p>
                              <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                                Submitted: {new Date(kit.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span 
                                className="px-3 py-1 text-xs rounded-full font-medium"
                                style={{ 
                                  backgroundColor: 
                                    kit.status === 'pending' ? '#FFF3E0' :
                                    kit.status === 'verified' ? '#E8F5E9' :
                                    kit.status === 'ordered' ? '#E3F2FD' :
                                    kit.status === 'delivered' ? '#F3E5F5' :
                                    '#F5F5F5',
                                  color:
                                    kit.status === 'pending' ? '#E65100' :
                                    kit.status === 'verified' ? '#2E7D32' :
                                    kit.status === 'ordered' ? '#1565C0' :
                                    kit.status === 'delivered' ? '#7B1FA2' :
                                    '#616161'
                                }}
                              >
                                {kit.status.charAt(0).toUpperCase() + kit.status.slice(1)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <p className="text-sm font-medium mb-2" style={{ color: '#163329' }}>Kit Items ({kit.items?.length || 0}):</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {kit.items?.map((item, idx) => (
                                <div 
                                  key={idx} 
                                  className="flex items-center justify-between p-2 rounded text-sm"
                                  style={{ backgroundColor: '#F0EFEA' }}
                                >
                                  <div>
                                    <span className="font-medium" style={{ color: '#163329' }}>
                                      {item.quantity}x
                                    </span>
                                    <span className="ml-1" style={{ color: '#5C635A' }}>{item.name}</span>
                                    {item.description && (
                                      <span className="text-xs ml-1" style={{ color: '#888' }}>({item.description})</span>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <span className="text-xs" style={{ color: '#5C635A' }}>
                                      <Usd value={(item.unit_price || 0)} /> each
                                    </span>
                                    <span className="ml-2 font-medium" style={{ color: '#D86B47' }}>
                                      <Usd value={(item.item_total || (item.quantity * (item.unit_price || 0)))} />
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-2 p-2 rounded text-right" style={{ backgroundColor: '#163329' }}>
                              <span className="text-white font-medium">Total Kit Price: </span>
                              <span className="text-lg font-bold" style={{ color: '#D86B47' }}>
                                <Usd value={(kit.total_kit_price || 0)} />
                              </span>
                            </div>
                          </div>
                          
                          {kit.admin_notes && (
                            <div className="mb-3 p-2 rounded" style={{ backgroundColor: '#E3F2FD' }}>
                              <p className="text-xs font-medium" style={{ color: '#1565C0' }}>Admin Notes:</p>
                              <p className="text-sm" style={{ color: '#1976D2' }}>{kit.admin_notes}</p>
                            </div>
                          )}
                          
                          <Dialog open={selectedKit === kit.id} onOpenChange={(open) => {
                            setSelectedKit(open ? kit.id : null);
                            if (!open) {
                              setKitStatus('');
                              setKitNotes('');
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                className="rounded-full"
                                
                              >
                                <Edit2 className="h-4 w-4 mr-1" />
                                Update Status
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                                  Update Kit Status
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 pt-4">
                                <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                                  <p className="text-sm"><strong>Course:</strong> {kit.course_title}</p>
                                  <p className="text-sm"><strong>Teacher:</strong> {kit.teacher_name}</p>
                                  <p className="text-sm"><strong>Items:</strong> {kit.items?.length || 0} items</p>
                                </div>
                                
                                <div>
                                  <Label>Status</Label>
                                  <select
                                    value={kitStatus}
                                    onChange={(e) => setKitStatus(e.target.value)}
                                    className="w-full mt-1 p-2 border rounded"
                                    style={{ borderColor: '#E3E1DB' }}
                                  >
                                    <option value="">Select status...</option>
                                    <option value="verified">Verified (Price Approved)</option>
                                    <option value="ordered">Ordered</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="pending">Pending</option>
                                  </select>
                                  <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                                    Select "Verified" to approve the kit and notify the teacher to proceed with tutorials.
                                  </p>
                                </div>
                                
                                <div>
                                  <Label>Notes (optional)</Label>
                                  <Textarea
                                    value={kitNotes}
                                    onChange={(e) => setKitNotes(e.target.value)}
                                    placeholder="Add notes about this kit order..."
                                    rows={3}
                                  />
                                </div>
                                
                                <Button
                                  onClick={() => handleUpdateKitStatus(kit.id)}
                                  disabled={isUpdating || !kitStatus}
                                  className="w-full"
                                  
                                >
                                  {isUpdating ? 'Updating...' : 'Update Status'}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="space-y-6">
                {/* Admin Add Product */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="h-5 w-5" style={{ color: '#163329' }} />
                        Marketplace Management
                      </div>
                    </CardTitle>
                    <Button
                      onClick={handleOpenSlotManagement}
                      variant="outline"
                      style={{ borderColor: '#163329', color: '#163329' }}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Slots
                    </Button>
                  </CardHeader>
                </Card>

                {/* Landing Page Slot Manager */}
                <Card 
                  style={{ borderColor: '#E3E1DB', cursor: 'pointer' }}
                  onClick={() => navigate('/admin/landing-slots')}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <Settings className="h-5 w-5" />
                      Ribbon Marketplace Manager
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      Manage the ribbon, featured products, and new arrivals sections on the landing page. Control which products appear and in what order.
                    </p>
                  </CardContent>
                </Card>

                {/* Landing Page Stats */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <TrendingUp className="h-5 w-5" />
                      Landing Page Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                      Update the stats displayed on the landing page (shown below the "Join now" button).
                    </p>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-sm font-medium" style={{ color: '#163329' }}>Views</Label>
                        <Input
                          value={landingStatsEdit.views}
                          onChange={(e) => setLandingStatsEdit(prev => ({ ...prev, views: e.target.value }))}
                          placeholder="e.g., 300K+"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium" style={{ color: '#163329' }}>Enrollments</Label>
                        <Input
                          value={landingStatsEdit.enrollments}
                          onChange={(e) => setLandingStatsEdit(prev => ({ ...prev, enrollments: e.target.value }))}
                          placeholder="e.g., 500+"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleUpdateLandingStats}
                      disabled={savingStats}
                      className="rounded-full"
                      style={{ backgroundColor: '#163329' }}
                    >
                      {savingStats ? 'Saving...' : 'Update Stats'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Pending Products */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5" style={{ color: '#E2A761' }} />
                        Pending Product Approvals
                        {pendingProducts.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: '#D86B47' }}>
                            {pendingProducts.length}
                          </span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pendingProducts.length === 0 ? (
                      <p className="text-center py-8" style={{ color: '#5C635A' }}>No products pending approval</p>
                    ) : (
                      <div className="space-y-4">
                        {pendingProducts.map((product) => (
                          <div key={product.id} className="p-4 rounded-lg border" style={{ borderColor: '#E3E1DB' }}>
                            <div className="flex gap-4">
                              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                {product.images && product.images.length > 0 ? (
                                  <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="h-8 w-8" style={{ color: '#E3E1DB' }} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <h4 className="font-semibold" style={{ color: '#163329' }}>{product.title}</h4>
                                    <p className="text-sm" style={{ color: '#5C635A' }}>
                                      By {product.seller_name} ({product.seller_email})
                                    </p>
                                  </div>
                                  <span className="font-bold text-lg" style={{ color: '#163329' }}>
                                    <Usd value={product.price} />
                                  </span>
                                </div>
                                <p className="text-sm mt-2 line-clamp-2" style={{ color: '#5C635A' }}>
                                  {product.description}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="px-2 py-0.5 rounded-full text-xs capitalize" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
                                    {product.category}
                                  </span>
                                  {product.is_digital ? (
                                    <span className="px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}>
                                      Digital
                                    </span>
                                  ) : (
                                    <span className="text-xs" style={{ color: '#5C635A' }}>Stock: {product.stock}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                              <Button
                                onClick={() => handleApproveProduct(product.id)}
                                className="flex-1 rounded-full"
                                style={{ backgroundColor: '#163329' }}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleRejectProduct(product.id)}
                                variant="outline"
                                className="flex-1 rounded-full text-red-500 border-red-500 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Pending Teacher Announcements */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-5 w-5" style={{ color: '#E2A761' }} />
                        Pending Announcements
                        {pendingAnnouncements.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: '#D86B47' }}>
                            {pendingAnnouncements.length}
                          </span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pendingAnnouncements.length === 0 ? (
                      <p className="text-center py-8" style={{ color: '#5C635A' }}>No announcements pending approval</p>
                    ) : (
                      <div className="space-y-4">
                        {pendingAnnouncements.map((announcement) => (
                          <div key={announcement.id} className="p-4 rounded-lg border" style={{ borderColor: '#E3E1DB' }}>
                            <div className="flex gap-4">
                              {announcement.image_url && (
                                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                  <img src={announcement.image_url} alt={announcement.title} className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="flex-1">
                                <h4 className="font-semibold" style={{ color: '#163329' }}>{announcement.title}</h4>
                                <p className="text-sm" style={{ color: '#5C635A' }}>
                                  By {announcement.teacher_name} ({announcement.teacher_email})
                                </p>
                                {announcement.description && (
                                  <p className="text-sm mt-2 line-clamp-2" style={{ color: '#5C635A' }}>
                                    {announcement.description}
                                  </p>
                                )}
                                {announcement.link_url && (
                                  <a 
                                    href={announcement.link_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs mt-1 inline-block underline"
                                    style={{ color: '#163329' }}
                                  >
                                    View Link
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                              <Button
                                onClick={() => handleApproveAnnouncement(announcement.id)}
                                className="flex-1 rounded-full"
                                style={{ backgroundColor: '#163329' }}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleRejectAnnouncement(announcement.id)}
                                variant="outline"
                                className="flex-1 rounded-full text-red-500 border-red-500 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Approved Announcements (Delete only) */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-5 w-5" style={{ color: '#2E7D32' }} />
                        Approved Announcements
                        {allAnnouncements.filter(a => a.status === 'approved').length > 0 && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: '#2E7D32' }}>
                            {allAnnouncements.filter(a => a.status === 'approved').length}
                          </span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {allAnnouncements.filter(a => a.status === 'approved').length === 0 ? (
                      <p className="text-center py-8" style={{ color: '#5C635A' }}>No approved announcements</p>
                    ) : (
                      <div className="space-y-4">
                        {allAnnouncements.filter(a => a.status === 'approved').map((announcement) => (
                          <div key={announcement.id} className="p-4 rounded-lg border" style={{ borderColor: '#E3E1DB' }}>
                            <div className="flex gap-4">
                              {announcement.image_url && (
                                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                  <img src={announcement.image_url} alt={announcement.title} className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="flex-1">
                                <h4 className="font-semibold" style={{ color: '#163329' }}>{announcement.title}</h4>
                                <p className="text-sm" style={{ color: '#5C635A' }}>
                                  By {announcement.teacher_name} ({announcement.teacher_email})
                                </p>
                                {announcement.description && (
                                  <p className="text-sm mt-2 line-clamp-2" style={{ color: '#5C635A' }}>
                                    {announcement.description}
                                  </p>
                                )}
                                {announcement.link_url && (
                                  <a 
                                    href={announcement.link_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs mt-1 inline-block underline"
                                    style={{ color: '#163329' }}
                                  >
                                    View Link
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                              <Button
                                onClick={() => handleDeleteAnnouncement(announcement.id)}
                                variant="outline"
                                className="flex-1 rounded-full text-red-500 border-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* All Products (Edit/Delete) */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5" style={{ color: '#163329' }} />
                        All Products
                        {allProducts.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: '#163329' }}>
                            {allProducts.length}
                          </span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {allProducts.length === 0 ? (
                      <p className="text-center py-8" style={{ color: '#5C635A' }}>No products yet. Create one using the "Add Product" button above.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Product</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Category</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Price</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Stock</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Status</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allProducts.map((product) => (
                              <tr key={product.id} className="border-b hover:bg-gray-50" style={{ borderColor: '#E3E1DB' }}>
                                <td className="py-3 px-2">
                                  <div className="flex items-center gap-3">
                                    {product.images?.[0] ? (
                                      <img src={product.images[0]} alt={product.title} className="w-10 h-10 object-cover rounded" />
                                    ) : (
                                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                        <Package className="h-5 w-5 text-gray-400" />
                                      </div>
                                    )}
                                    <div>
                                      <p className="font-medium" style={{ color: '#163329' }}>{product.title || product.name}</p>
                                      <p className="text-xs" style={{ color: '#5C635A' }}>{product.seller_name || 'Admin'}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-2 capitalize" style={{ color: '#5C635A' }}>{product.category}</td>
                                <td className="py-3 px-2 font-medium" style={{ color: '#163329' }}><Usd value={product.price} /></td>
                                <td className="py-3 px-2" style={{ color: '#5C635A' }}>
                                  {product.is_digital ? 'Digital' : product.stock}
                                </td>
                                <td className="py-3 px-2">
                                  <Badge style={{ 
                                    backgroundColor: product.is_approved ? '#E8F5E9' : '#FFF3E0',
                                    color: product.is_approved ? '#2E7D32' : '#E65100'
                                  }}>
                                    {product.is_approved ? 'Approved' : 'Pending'}
                                  </Badge>
                                </td>
                                <td className="py-3 px-2">
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openEditProductDialog(product)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteProduct(product.id, product.title || product.name)}
                                      className="h-8 w-8 p-0 text-red-500 border-red-300 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Insight Warehouse Orders */}
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="h-5 w-5" />
                        Recent Warehouse Orders
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {marketplaceOrders.length === 0 ? (
                      <p className="text-center py-8" style={{ color: '#5C635A' }}>No orders yet</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b" style={{ borderColor: '#E3E1DB' }}>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Order ID</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Buyer</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Items</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Total</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Status</th>
                              <th className="text-left py-3 px-2" style={{ color: '#5C635A' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {marketplaceOrders.map((order) => (
                              <tr key={order.id} className="border-b" style={{ borderColor: '#E3E1DB' }}>
                                <td className="py-3 px-2 font-mono text-xs" style={{ color: '#163329' }}>
                                  #{order.id?.slice(0, 8)}
                                </td>
                                <td className="py-3 px-2">
                                  <div>
                                    <p style={{ color: '#163329' }}>{order.buyer_name}</p>
                                    <p className="text-xs" style={{ color: '#5C635A' }}>{order.buyer_email}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-2" style={{ color: '#163329' }}>
                                  {order.items?.length} item(s)
                                </td>
                                <td className="py-3 px-2 font-semibold" style={{ color: '#163329' }}>
                                  <Usd value={order.total} />
                                </td>
                                <td className="py-3 px-2">
                                  <span 
                                    className="px-2 py-0.5 rounded-full text-xs capitalize"
                                    style={{ 
                                      backgroundColor: order.status === 'paid' ? '#E8F5E9' : 
                                                       order.status === 'shipped' ? '#E3F2FD' :
                                                       order.status === 'delivered' ? '#F3E5F5' : '#FFF8E1',
                                      color: order.status === 'paid' ? '#2E7D32' : 
                                             order.status === 'shipped' ? '#1976D2' :
                                             order.status === 'delivered' ? '#7B1FA2' : '#F9A825'
                                    }}
                                  >
                                    {order.status}
                                  </span>
                                </td>
                                <td className="py-3 px-2">
                                  {order.has_physical && order.status === 'paid' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUpdateOrderStatus(order.id, 'shipped')}
                                      className="rounded-full text-xs"
                                    >
                                      Mark Shipped
                                    </Button>
                                  )}
                                  {order.status === 'shipped' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                                      className="rounded-full text-xs"
                                    >
                                      Mark Delivered
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </TabsContent>

          {/* Declarations Tab */}
          <TabsContent value="declarations">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    Pending Course Declarations
                  </CardTitle>
                  <p className="text-sm" style={{ color: '#5C635A' }}>
                    Review and approve acknowledgment declarations that teachers have created for their courses. 
                    Learners must accept approved declarations before enrolling.
                  </p>
                </CardHeader>
                <CardContent>
                  {pendingDeclarations.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto mb-3" style={{ color: '#5C635A' }} />
                      <p className="text-lg font-medium" style={{ color: '#163329' }}>No Pending Declarations</p>
                      <p className="text-sm" style={{ color: '#5C635A' }}>
                        All course declarations have been reviewed.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingDeclarations.map((declaration) => (
                        <div 
                          key={declaration.id}
                          className="p-4 rounded-lg border"
                          style={{ backgroundColor: '#FFF8E7', borderColor: '#F59E0B' }}
                          data-testid={`pending-declaration-${declaration.id}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="h-5 w-5" style={{ color: '#B45309' }} />
                                <h4 className="font-semibold" style={{ color: '#163329' }}>
                                  {declaration.course_title}
                                </h4>
                                <span 
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                                >
                                  Pending
                                </span>
                              </div>
                              <p className="text-sm mb-2" style={{ color: '#5C635A' }}>
                                <span className="font-medium">Teacher:</span> {declaration.teacher_name} ({declaration.teacher_email})
                              </p>
                              <p className="text-xs mb-3" style={{ color: '#5C635A' }}>
                                Submitted: {new Date(declaration.created_at).toLocaleString()}
                              </p>
                              
                              {/* Declaration Text Preview */}
                              <div 
                                className="p-3 rounded-lg text-sm max-h-40 overflow-y-auto whitespace-pre-wrap"
                                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3E1DB', color: '#163329' }}
                              >
                                {declaration.text}
                              </div>
                            </div>
                            
                            <div className="flex flex-row md:flex-col gap-2">
                              <Button
                                onClick={() => handleApproveDeclaration(declaration.id)}
                                disabled={isApprovingDeclaration === declaration.id}
                                className="flex-1 md:flex-none"
                                style={{ backgroundColor: '#2E7D32' }}
                                data-testid={`approve-declaration-${declaration.id}`}
                              >
                                {isApprovingDeclaration === declaration.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <Check className="h-4 w-4 mr-1" />
                                )}
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleRejectDeclaration(declaration.id)}
                                disabled={isRejectingDeclaration === declaration.id}
                                variant="destructive"
                                className="flex-1 md:flex-none"
                                data-testid={`reject-declaration-${declaration.id}`}
                              >
                                {isRejectingDeclaration === declaration.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <X className="h-4 w-4 mr-1" />
                                )}
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <PendingModelLettersCard />
            </motion.div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Users List */}
                <Card style={{ borderColor: '#E3E1DB' }} className="md:col-span-1">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[500px] overflow-y-auto">
                      {chatUsers.map((user) => (
                        <div
                          key={user.id}
                          className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedChatUser?.id === user.id ? 'bg-gray-100' : ''}`}
                          style={{ borderColor: '#E3E1DB' }}
                          onClick={() => handleSelectChatUser(user)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium" style={{ color: '#163329' }}>{user.name}</p>
                              <p className="text-xs" style={{ color: '#5C635A' }}>
                                {user.role === 'teacher' ? 'Teacher' : 'Student'} • {user.email}
                              </p>
                            </div>
                            {user.unread_count > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: '#D86B47', color: 'white' }}>
                                {user.unread_count}
                              </span>
                            )}
                          </div>
                          {!user.is_active && (
                            <span className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                              Deactivated
                            </span>
                          )}
                        </div>
                      ))}
                      {chatUsers.length === 0 && (
                        <p className="text-center py-8" style={{ color: '#5C635A' }}>No users to chat with</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Chat Area */}
                <Card style={{ borderColor: '#E3E1DB' }} className="md:col-span-2">
                  <CardHeader className="border-b" style={{ borderColor: '#E3E1DB' }}>
                    <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      {selectedChatUser ? (
                        <div className="flex items-center gap-2">
                          <MessageCircle className="h-5 w-5" />
                          Chat with {selectedChatUser.name}
                          <span className="text-xs font-normal px-2 py-1 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
                            {selectedChatUser.role}
                          </span>
                        </div>
                      ) : 'Select a user to chat'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {selectedChatUser ? (
                      <>
                        <div className="h-[350px] overflow-y-auto p-4 space-y-3">
                          {chatMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[70%] p-3 rounded-lg ${msg.sender_type === 'admin' ? 'rounded-br-none' : 'rounded-bl-none'}`}
                                style={{
                                  backgroundColor: msg.sender_type === 'admin' ? '#163329' : '#F0EFEA',
                                  color: msg.sender_type === 'admin' ? '#FAF9F6' : '#163329'
                                }}
                              >
                                <p className="text-sm">{msg.content}</p>
                                <p className="text-xs mt-1 opacity-70">
                                  {new Date(msg.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                          {chatMessages.length === 0 && (
                            <p className="text-center py-8" style={{ color: '#5C635A' }}>No messages yet. Start a conversation!</p>
                          )}
                        </div>
                        <div className="p-4 border-t flex gap-2" style={{ borderColor: '#E3E1DB' }}>
                          <Input
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          />
                          <Button
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim()}
                            
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-center py-16" style={{ color: '#5C635A' }}>
                        Select a user from the list to start chatting
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                  <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Shield className="inline-block mr-2 h-5 w-5" />
                    Admin Management
                  </CardTitle>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleOpenPromoteDialog}
                      variant="outline"
                      style={{ borderColor: '#163329', color: '#163329' }}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Promote User
                    </Button>
                    <Button
                      onClick={() => setShowAddAdminDialog(true)}
                      style={{ backgroundColor: '#163329' }}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Admin
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-6" style={{ color: '#5C635A' }}>
                    Manage admin accounts that have access to this dashboard. Only admins can view and access admin features.
                  </p>
                  
                  {adminUsers.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No admin users found</p>
                  ) : (
                    <div className="space-y-3">
                      {adminUsers.map((admin) => (
                        <div 
                          key={admin.id}
                          className="flex items-center justify-between p-4 rounded-lg"
                          style={{ backgroundColor: '#F0EFEA' }}
                        >
                          <div className="flex items-center gap-4">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: '#163329' }}
                            >
                              <Shield className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold" style={{ color: '#163329' }}>
                                {admin.is_primary ? 'Primary Platform Admin' : admin.name}
                              </p>
                              <p className="text-sm" style={{ color: '#5C635A' }}>{admin.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!admin.is_primary && (
                              <Badge 
                                className="text-xs"
                                style={{ 
                                  backgroundColor: admin.was_promoted ? '#FFF3E0' : '#E3F2FD',
                                  color: admin.was_promoted ? '#E65100' : '#1565C0'
                                }}
                              >
                                {admin.was_promoted ? 'Promoted' : 'Admin'}
                              </Badge>
                            )}
                            {!admin.is_primary && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>

        {/* Add Admin Dialog */}
        <Dialog open={showAddAdminDialog} onOpenChange={setShowAddAdminDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <UserPlus className="inline-block mr-2 h-5 w-5" />
                Add New Admin
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="admin-name">Full Name</Label>
                <Input
                  id="admin-name"
                  value={newAdminName}
                  onChange={(e) => setNewAdminName(e.target.value)}
                  placeholder="Enter admin name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="admin-email">Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="Enter admin email"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  placeholder="Enter password (min 8 characters)"
                  className="mt-1"
                />
                <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                  Min 8 characters with at least one uppercase letter
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddAdminDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateAdmin}
                  disabled={isCreatingAdmin}
                  className="flex-1"
                  style={{ backgroundColor: '#163329' }}
                >
                  {isCreatingAdmin ? 'Creating...' : 'Create Admin'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Promote User to Admin Dialog */}
        <Dialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <TrendingUp className="inline-block mr-2 h-5 w-5" />
                Promote User to Admin
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-sm" style={{ color: '#5C635A' }}>
                Select an existing user to grant them admin privileges. They will retain access to their original role features.
              </p>
              
              {/* Search Input */}
              <div>
                <Label htmlFor="user-search">Search Users</Label>
                <Input
                  id="user-search"
                  value={promotableSearchQuery}
                  onChange={(e) => setPromotableSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="mt-1"
                />
              </div>
              
              {/* User List */}
              <div className="max-h-64 overflow-y-auto border rounded-lg" style={{ borderColor: '#E3E1DB' }}>
                {promotableUsers.length === 0 ? (
                  <p className="text-center py-8 text-sm" style={{ color: '#5C635A' }}>
                    No users available to promote
                  </p>
                ) : (
                  <div className="divide-y" style={{ borderColor: '#E3E1DB' }}>
                    {promotableUsers
                      .filter(user => 
                        promotableSearchQuery === '' ||
                        user.name?.toLowerCase().includes(promotableSearchQuery.toLowerCase()) ||
                        user.email?.toLowerCase().includes(promotableSearchQuery.toLowerCase())
                      )
                      .map((user) => (
                        <div 
                          key={user.id}
                          onClick={() => setSelectedUserToPromote(user.id)}
                          className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedUserToPromote === user.id ? 'bg-green-50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                              style={{ backgroundColor: user.role === 'teacher' ? '#D86B47' : user.role === 'seller' ? '#6B5B95' : '#163329' }}
                            >
                              {user.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-medium text-sm" style={{ color: '#163329' }}>{user.name}</p>
                              <p className="text-xs" style={{ color: '#5C635A' }}>{user.email}</p>
                            </div>
                          </div>
                          <Badge 
                            className="text-xs capitalize"
                            style={{ 
                              backgroundColor: user.role === 'teacher' ? '#FFF3E0' : user.role === 'seller' ? '#F3E5F5' : '#E8F5E9',
                              color: user.role === 'teacher' ? '#E65100' : user.role === 'seller' ? '#6B5B95' : '#2E7D32'
                            }}
                          >
                            {user.role === 'student' ? 'Learner' : user.role}
                          </Badge>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              
              {selectedUserToPromote && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7' }}>
                  <p className="text-sm" style={{ color: '#2E7D32' }}>
                    <CheckCircle className="inline-block mr-1 h-4 w-4" />
                    Selected: {promotableUsers.find(u => u.id === selectedUserToPromote)?.name}
                  </p>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPromoteDialog(false);
                    setSelectedUserToPromote('');
                    setPromotableSearchQuery('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePromoteToAdmin}
                  disabled={isPromoting || !selectedUserToPromote}
                  className="flex-1"
                  style={{ backgroundColor: '#163329' }}
                >
                  {isPromoting ? 'Promoting...' : 'Promote to Admin'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Deactivate Account Dialog */}
        <Dialog open={!!showDeactivateDialog} onOpenChange={(open) => !open && setShowDeactivateDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#C62828' }}>
                Deactivate Account
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p style={{ color: '#5C635A' }}>
                This will prevent the user from logging into their account. They will see a message that their account has been deactivated.
              </p>
              <div>
                <Label htmlFor="deactivation-reason">Reason (optional)</Label>
                <Textarea
                  id="deactivation-reason"
                  value={deactivationReason}
                  onChange={(e) => setDeactivationReason(e.target.value)}
                  placeholder="Enter reason for deactivation..."
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeactivateDialog(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeactivateAccount}
                  disabled={isUpdating}
                  className="flex-1"
                  style={{ backgroundColor: '#C62828', color: 'white' }}
                >
                  {isUpdating ? 'Deactivating...' : 'Deactivate Account'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bank Details Dialog */}
        <Dialog open={!!showBankDetails} onOpenChange={(open) => !open && setShowBankDetails(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>Bank Details</DialogTitle>
            </DialogHeader>
            {bankDetailsData && (
              <div className="space-y-4 pt-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                  <p className="text-sm" style={{ color: '#5C635A' }}>Teacher</p>
                  <p className="font-medium" style={{ color: '#163329' }}>{bankDetailsData.teacher_name}</p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>{bankDetailsData.teacher_email}</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Bank Name</p>
                    <p className="font-medium" style={{ color: '#163329' }}>{bankDetailsData.bank_name}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Account Holder</p>
                    <p className="font-medium" style={{ color: '#163329' }}>{bankDetailsData.account_holder_name}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Account Number</p>
                    <p className="font-medium font-mono" style={{ color: '#163329' }}>{bankDetailsData.account_number}</p>
                  </div>
                  {bankDetailsData.routing_number && (
                    <div>
                      <p className="text-sm" style={{ color: '#5C635A' }}>Routing Number</p>
                      <p className="font-medium font-mono" style={{ color: '#163329' }}>{bankDetailsData.routing_number}</p>
                    </div>
                  )}
                  {bankDetailsData.swift_code && (
                    <div>
                      <p className="text-sm" style={{ color: '#5C635A' }}>SWIFT Code</p>
                      <p className="font-medium font-mono" style={{ color: '#163329' }}>{bankDetailsData.swift_code}</p>
                    </div>
                  )}
                </div>
                
                {!bankDetailsData.bank_verified && (
                  <Button
                    onClick={() => handleVerifyBank(showBankDetails)}
                    className="w-full"
                    
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Mark as Verified
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Admin Bank Details Dialog */}
        <Dialog open={showAdminBankForm} onOpenChange={setShowAdminBankForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                Platform Bank Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-sm" style={{ color: '#5C635A' }}>
                Enter the bank account where student payments will be deposited.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="admin-bank-name">Bank Name *</Label>
                <Input
                  id="admin-bank-name"
                  value={adminBankForm.bank_name}
                  onChange={(e) => setAdminBankForm({ ...adminBankForm, bank_name: e.target.value })}
                  placeholder="e.g., Chase Bank"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-account-holder">Account Holder Name *</Label>
                <Input
                  id="admin-account-holder"
                  value={adminBankForm.account_holder_name}
                  onChange={(e) => setAdminBankForm({ ...adminBankForm, account_holder_name: e.target.value })}
                  placeholder="Name on account"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-account-number">Account Number *</Label>
                <Input
                  id="admin-account-number"
                  type="password"
                  value={adminBankForm.account_number}
                  onChange={(e) => setAdminBankForm({ ...adminBankForm, account_number: e.target.value })}
                  placeholder="Enter account number"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-routing">Routing Number</Label>
                  <Input
                    id="admin-routing"
                    value={adminBankForm.routing_number}
                    onChange={(e) => setAdminBankForm({ ...adminBankForm, routing_number: e.target.value })}
                    placeholder="9-digit"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-swift">SWIFT Code</Label>
                  <Input
                    id="admin-swift"
                    value={adminBankForm.swift_code}
                    onChange={(e) => setAdminBankForm({ ...adminBankForm, swift_code: e.target.value })}
                    placeholder="For international"
                  />
                </div>
              </div>
              
              <Button
                onClick={handleSaveAdminBankDetails}
                disabled={isUpdating}
                className="w-full"
                
              >
                {isUpdating ? 'Saving...' : 'Save Bank Details'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin Create Product Dialog */}
        <Dialog open={showCreateProductDialog} onOpenChange={setShowCreateProductDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <Package className="inline-block mr-2 h-5 w-5" />
                Add New Product
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProduct} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="admin-product-title">Product Title *</Label>
                <Input
                  id="admin-product-title"
                  value={productFormData.title}
                  onChange={(e) => setProductFormData({...productFormData, title: e.target.value})}
                  placeholder="Enter product title"
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="admin-product-desc">Description *</Label>
                <Textarea
                  id="admin-product-desc"
                  value={productFormData.description}
                  onChange={(e) => setProductFormData({...productFormData, description: e.target.value})}
                  placeholder="Describe your product"
                  className="mt-1"
                  rows={3}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="admin-product-price">Price (USD) *</Label>
                  <Input
                    id="admin-product-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={productFormData.price}
                    onChange={(e) => setProductFormData({...productFormData, price: e.target.value})}
                    placeholder="0.00"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin-product-category">Category *</Label>
                  <select
                    id="admin-product-category"
                    value={productFormData.category}
                    onChange={(e) => setProductFormData({...productFormData, category: e.target.value})}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                    required
                  >
                    <option value="books">Books</option>
                    <option value="electronics">Electronics</option>
                    <option value="teaching_aids">Teaching Aids</option>
                    <option value="stationery">Stationery</option>
                    <option value="digital">Digital Products</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="admin-is-digital"
                  checked={productFormData.is_digital}
                  onCheckedChange={(checked) => setProductFormData({...productFormData, is_digital: checked})}
                />
                <Label htmlFor="admin-is-digital">Digital Product (no shipping)</Label>
              </div>
              
              {productFormData.is_digital ? (
                <div>
                  <Label htmlFor="admin-download-url">Download URL *</Label>
                  <Input
                    id="admin-download-url"
                    type="url"
                    value={productFormData.download_url}
                    onChange={(e) => setProductFormData({...productFormData, download_url: e.target.value})}
                    placeholder="https://example.com/file.pdf"
                    className="mt-1"
                  />
                </div>
              ) : (
                <div>
                  <Label htmlFor="admin-stock">Stock Quantity *</Label>
                  <Input
                    id="admin-stock"
                    type="number"
                    min="0"
                    value={productFormData.stock}
                    onChange={(e) => setProductFormData({...productFormData, stock: e.target.value})}
                    placeholder="10"
                    className="mt-1"
                    required={!productFormData.is_digital}
                  />
                </div>
              )}
              
              {/* Product Image Upload */}
              <div>
                <Label>Product Image</Label>
                <div 
                  className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500 transition-colors"
                  style={{ borderColor: productImagePreview ? '#163329' : '#E3E1DB' }}
                  onClick={() => productImageInputRef.current?.click()}
                >
                  <input
                    ref={productImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProductImageSelect}
                    className="hidden"
                  />
                  {productImagePreview ? (
                    <div className="space-y-2">
                      <img 
                        src={productImagePreview} 
                        alt="Preview" 
                        className="w-32 h-32 object-cover mx-auto rounded-lg"
                      />
                      <p className="text-sm" style={{ color: '#163329' }}>
                        Click to change image
                      </p>
                    </div>
                  ) : (
                    <div className="py-4">
                      <Upload className="h-10 w-10 mx-auto mb-2" style={{ color: '#5C635A' }} />
                      <p className="text-sm font-medium" style={{ color: '#163329' }}>
                        Click to upload product image
                      </p>
                      <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                        PNG, JPG up to 5MB
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowCreateProductDialog(false);
                    resetProductForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isCreatingProduct}
                  style={{ backgroundColor: '#163329' }}
                >
                  {isCreatingProduct ? 'Creating...' : 'Create Product'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Product Dialog */}
        <Dialog open={showEditProductDialog} onOpenChange={setShowEditProductDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <Edit2 className="inline-block mr-2 h-5 w-5" />
                Edit Product
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateProduct} className="space-y-4 pt-4">
              <div>
                <Label>Product Title *</Label>
                <Input
                  required
                  value={editProductFormData.title}
                  onChange={(e) => setEditProductFormData({...editProductFormData, title: e.target.value})}
                  placeholder="Enter product title"
                />
              </div>
              
              <div>
                <Label>Description *</Label>
                <textarea
                  required
                  value={editProductFormData.description}
                  onChange={(e) => setEditProductFormData({...editProductFormData, description: e.target.value})}
                  className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                  placeholder="Product description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={editProductFormData.price}
                    onChange={(e) => setEditProductFormData({...editProductFormData, price: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select
                    value={editProductFormData.category}
                    onChange={(e) => setEditProductFormData({...editProductFormData, category: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="books">Books</option>
                    <option value="equipment">Equipment</option>
                    <option value="kits">Kits</option>
                    <option value="digital">Digital Downloads</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-digital"
                  checked={editProductFormData.is_digital}
                  onChange={(e) => setEditProductFormData({...editProductFormData, is_digital: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="edit-is-digital" className="cursor-pointer">Digital Product (unlimited stock)</Label>
              </div>
              
              {!editProductFormData.is_digital && (
                <div>
                  <Label>Stock Quantity *</Label>
                  <Input
                    type="number"
                    min="0"
                    required={!editProductFormData.is_digital}
                    value={editProductFormData.stock}
                    onChange={(e) => setEditProductFormData({...editProductFormData, stock: e.target.value})}
                    placeholder="Available quantity"
                  />
                </div>
              )}
              
              {editProductFormData.is_digital && (
                <div>
                  <Label>Download URL</Label>
                  <Input
                    type="url"
                    value={editProductFormData.download_url}
                    onChange={(e) => setEditProductFormData({...editProductFormData, download_url: e.target.value})}
                    placeholder="https://example.com/file.pdf"
                  />
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditProductDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isUpdatingProduct}
                  style={{ backgroundColor: '#163329' }}
                >
                  {isUpdatingProduct ? 'Updating...' : 'Update Product'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Slot Management Dialog */}
        <Dialog open={showSlotManagement} onOpenChange={setShowSlotManagement}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <Settings className="inline-block mr-2 h-5 w-5" />
                Manage Product Slots
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              {/* Add Product Button */}
              <div className="flex justify-between items-center p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                <div>
                  <p className="font-medium" style={{ color: '#163329' }}>Create New Product</p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>Add a new product to the marketplace</p>
                </div>
                <Button
                  onClick={() => {
                    setShowSlotManagement(false);
                    setShowCreateProductDialog(true);
                  }}
                  style={{ backgroundColor: '#163329' }}
                  data-testid="admin-add-product-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
              
              {/* Mode Selection for Sections */}
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ color: '#163329' }}>
                    Slot Fill Mode
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm" style={{ color: '#5C635A' }}>
                    Choose how products fill slots: <strong>Automatic</strong> fills based on creation date, <strong>Manual</strong> lets you place each product.
                  </p>
                  
                  {/* Featured Products Mode */}
                  <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: '#E3E1DB' }}>
                    <div>
                      <p className="font-medium" style={{ color: '#163329' }}>Featured Products</p>
                      <p className="text-xs" style={{ color: '#5C635A' }}>Slots 1-{slotConfig.featured_total_slots} on landing page</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={featuredMode === 'automatic' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSlotMode('featured', 'automatic')}
                        disabled={isUpdatingMode || featuredMode === 'automatic'}
                        style={featuredMode === 'automatic' ? { backgroundColor: '#163329' } : {}}
                      >
                        Automatic
                      </Button>
                      <Button
                        size="sm"
                        variant={featuredMode === 'manual' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSlotMode('featured', 'manual')}
                        disabled={isUpdatingMode || featuredMode === 'manual'}
                        style={featuredMode === 'manual' ? { backgroundColor: '#163329' } : {}}
                      >
                        Manual
                      </Button>
                    </div>
                  </div>
                  
                  {/* Essential Class Mode */}
                  <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: '#E3E1DB' }}>
                    <div>
                      <p className="font-medium" style={{ color: '#163329' }}>Essential Class</p>
                      <p className="text-xs" style={{ color: '#5C635A' }}>Slots {slotConfig.featured_total_slots + 1}-{slotConfig.featured_total_slots + slotConfig.essential_total_slots} on landing page</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={essentialMode === 'automatic' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSlotMode('new_arrivals', 'automatic')}
                        disabled={isUpdatingMode || essentialMode === 'automatic'}
                        style={essentialMode === 'automatic' ? { backgroundColor: '#163329' } : {}}
                      >
                        Automatic
                      </Button>
                      <Button
                        size="sm"
                        variant={essentialMode === 'manual' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSlotMode('new_arrivals', 'manual')}
                        disabled={isUpdatingMode || essentialMode === 'manual'}
                        style={essentialMode === 'manual' ? { backgroundColor: '#163329' } : {}}
                      >
                        Manual
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <p className="text-sm" style={{ color: '#5C635A' }}>
                Products auto-fill slots in sequence: Featured Products → Essential Class → View All.
                Use this to manually override and place specific products in specific slots.
              </p>
              
              {/* Assignment Form */}
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ color: '#163329' }}>
                    Assign Product to Slot
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Select Product</Label>
                      <select
                        value={selectedProductForSlot || ''}
                        onChange={(e) => setSelectedProductForSlot(e.target.value)}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="">-- Select Product --</option>
                        {availableProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.title} ({formatUsdZar(p.price)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Section</Label>
                      <select
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e.target.value)}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="featured">Featured Products</option>
                        <option value="new_arrivals">Essential Class</option>
                      </select>
                    </div>
                    <div>
                      <Label>Slot Number</Label>
                      <select
                        value={selectedSlotNumber}
                        onChange={(e) => setSelectedSlotNumber(parseInt(e.target.value))}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        {Array.from({ length: selectedSection === 'featured' ? slotConfig.featured_total_slots : slotConfig.essential_total_slots }, (_, i) => (
                          <option key={i + 1} value={i + 1}>Slot {i + 1}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <Button
                    onClick={handleAssignProductToSlot}
                    disabled={isAssigningSlot || !selectedProductForSlot}
                    style={{ backgroundColor: '#163329' }}
                  >
                    {isAssigningSlot ? 'Assigning...' : 'Assign to Slot'}
                  </Button>
                </CardContent>
              </Card>
              
              {/* Current Assignments */}
              <div className="space-y-4">
                <h4 className="font-semibold" style={{ color: '#163329' }}>Current Manual Overrides</h4>
                
                {availableProducts.filter(p => p.current_assignment).length === 0 ? (
                  <p className="text-sm py-4 text-center" style={{ color: '#5C635A' }}>
                    No manual overrides. All slots are auto-filling based on product creation order.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {availableProducts.filter(p => p.current_assignment).map((p) => (
                      <div 
                        key={p.id}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: '#F0EFEA' }}
                      >
                        <div className="flex items-center gap-3">
                          {p.image && (
                            <img 
                              src={p.image} 
                              alt={p.title} 
                              className="w-10 h-10 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium text-sm" style={{ color: '#163329' }}>{p.title}</p>
                            <p className="text-xs" style={{ color: '#5C635A' }}>
                              {p.current_assignment.section === 'featured' ? 'Featured' : 'Essential Class'} - Slot {p.current_assignment.slot}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveOverride(p.current_assignment.section, p.current_assignment.slot)}
                          className="text-red-500 border-red-300 hover:bg-red-50"
                        >
                          Remove Override
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                <strong>Tip:</strong> Products without manual overrides will auto-fill slots in order of creation (newest first).
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
